import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CalendarCheck, PhoneIncoming, PhoneCall, CheckCircle2, Inbox as InboxIcon,
  Play, ChevronRight, Sparkles, RefreshCcw, Plus, FileText, ClipboardList,
  UserCheck, Repeat, MessageSquare, Mail, Clock, AlertCircle, PenLine,
  Webhook, StickyNote, Type, AtSign, Phone as PhoneI, Hash, ChevronDown,
  CalendarDays, ListChecks, Upload, PenTool, GripVertical, Bot, Search,
  BookOpen, FileQuestion, ShieldCheck, Receipt, MessagesSquare, Send,
  TrendingUp, TrendingDown, ArrowUpRight, MousePointer2, GitBranch,
  Activity, Users, DollarSign, MoreHorizontal, Paperclip,
  Smile, ZoomIn, ZoomOut, Maximize2, Settings2, Zap, GitMerge,
  Tag, Diamond, Headphones, Mic, ShieldAlert, Copy, Trash2, BarChart3,
  User, MapPin, Heart, CreditCard, Megaphone, GraduationCap, Award,
  Eye, MousePointerClick, Image,
} from "lucide-react";

/* ============================================================================
   SHARED — StatTile (used by multiple scenes)
   =========================================================================== */
const StatTile = ({ label, value, accent, sub }) => (
  <div className={`rounded-xl border border-border ${accent || "bg-card"} p-3.5`}>
    <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
      {label}
    </div>
    <div className="mt-1.5 font-display text-2xl font-black tracking-[-0.02em]">
      {value}
    </div>
    {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
  </div>
);

/* ============================================================================
   SCENE 1 — Dashboard
   =========================================================================== */

const Sparkline = ({ points, stroke = "#a78bfa", fill = "rgba(167,139,250,0.18)" }) => {
  const w = 88, h = 26;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const range = max - min || 1;
  const step = w / (points.length - 1);
  const pts = points.map((v, i) => `${i * step},${h - ((v - min) / range) * h}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-6 w-[88px]" preserveAspectRatio="none">
      <polyline points={`0,${h} ${pts} ${w},${h}`} fill={fill} stroke="none" />
      <polyline points={pts} fill="none" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const KPI = ({ Icon, label, value, delta, tone, spark, sparkStroke, sparkFill }) => {
  const isUp = tone === "emerald";
  const TrIcon = isUp ? TrendingUp : TrendingDown;
  return (
    <div className="relative overflow-hidden rounded-xl border border-border bg-card p-3.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          <Icon className="h-3 w-3" />
          {label}
        </div>
        <span className={`inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] font-bold ${
          isUp
            ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
            : "bg-rose-500/10 text-rose-600 dark:text-rose-400"
        }`}>
          <TrIcon className="h-2.5 w-2.5" /> {delta}
        </span>
      </div>
      <div className="mt-1.5 flex items-end justify-between gap-2">
        <div className="font-display text-2xl font-black tracking-[-0.02em]">{value}</div>
        <Sparkline points={spark} stroke={sparkStroke} fill={sparkFill} />
      </div>
    </div>
  );
};

/* SVG-based funnel area chart */
const FunnelChart = () => {
  const data = [
    { day: "Mon", leads: 14, booked: 9  },
    { day: "Tue", leads: 18, booked: 12 },
    { day: "Wed", leads: 16, booked: 11 },
    { day: "Thu", leads: 22, booked: 16 },
    { day: "Fri", leads: 25, booked: 19 },
    { day: "Sat", leads: 12, booked: 7  },
    { day: "Sun", leads: 8,  booked: 5  },
  ];
  const W = 400, H = 130, PAD_X = 8, PAD_Y = 8;
  const max = 28;
  const step = (W - PAD_X * 2) / (data.length - 1);

  const toPoints = (key) =>
    data.map((d, i) => [PAD_X + i * step, PAD_Y + (1 - d[key] / max) * (H - PAD_Y * 2)]);

  const leadsPts = toPoints("leads");
  const bookedPts = toPoints("booked");

  const buildArea = (pts) =>
    `M ${pts[0][0]} ${H - PAD_Y} L ${pts.map((p) => `${p[0]} ${p[1]}`).join(" L ")} L ${pts[pts.length - 1][0]} ${H - PAD_Y} Z`;
  const buildLine = (pts) => `M ${pts.map((p) => `${p[0]} ${p[1]}`).join(" L ")}`;

  return (
    <div className="flex h-full flex-col">
      <div className="relative flex-1">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-full w-full">
          <defs>
            <linearGradient id="gradLeads" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.45" />
              <stop offset="100%" stopColor="#a78bfa" stopOpacity="0.02" />
            </linearGradient>
            <linearGradient id="gradBooked" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#34d399" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#34d399" stopOpacity="0.02" />
            </linearGradient>
          </defs>
          {/* horizontal gridlines */}
          {[0.25, 0.5, 0.75].map((p) => (
            <line
              key={p}
              x1={PAD_X}
              x2={W - PAD_X}
              y1={PAD_Y + p * (H - PAD_Y * 2)}
              y2={PAD_Y + p * (H - PAD_Y * 2)}
              stroke="currentColor"
              strokeOpacity="0.08"
              strokeDasharray="2 3"
            />
          ))}
          {/* leads area */}
          <path d={buildArea(leadsPts)} fill="url(#gradLeads)" />
          <path d={buildLine(leadsPts)} fill="none" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          {/* booked area */}
          <path d={buildArea(bookedPts)} fill="url(#gradBooked)" />
          <path d={buildLine(bookedPts)} fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          {/* booked dots */}
          {bookedPts.map((p, i) => (
            <g key={i}>
              <circle cx={p[0]} cy={p[1]} r="3" fill="#10b981" />
              <circle cx={p[0]} cy={p[1]} r="1.2" fill="#fff" />
            </g>
          ))}
          {/* friday highlight callout */}
          <g>
            <line x1={leadsPts[4][0]} x2={leadsPts[4][0]} y1={leadsPts[4][1]} y2={H - PAD_Y} stroke="#a78bfa" strokeOpacity="0.35" strokeDasharray="2 2" />
            <circle cx={leadsPts[4][0]} cy={leadsPts[4][1]} r="3.5" fill="#a78bfa" />
            <circle cx={leadsPts[4][0]} cy={leadsPts[4][1]} r="1.4" fill="#fff" />
          </g>
        </svg>
        {/* Floating tooltip */}
        <div
          className="pointer-events-none absolute rounded-lg border border-border bg-card/95 px-2.5 py-1.5 shadow-lg backdrop-blur"
          style={{ left: `${(leadsPts[4][0] / W) * 100}%`, top: "8%", transform: "translateX(-50%)" }}
        >
          <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Fri · Jun 12</p>
          <div className="mt-0.5 flex items-center gap-2 text-[10px]">
            <span className="flex items-center gap-1 font-semibold">
              <span className="h-1.5 w-1.5 rounded-full bg-violet-500" /> 25 leads
            </span>
            <span className="flex items-center gap-1 font-semibold">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> 19 booked
            </span>
          </div>
        </div>
      </div>
      <div className="mt-1 flex justify-between px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {data.map((d) => <span key={d.day}>{d.day}</span>)}
      </div>
    </div>
  );
};

const ActivityRow = ({ Icon, who, what, time, tone }) => {
  const toneMap = {
    emerald: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
    violet:  "bg-violet-500/15 text-violet-600 dark:text-violet-400",
    amber:   "bg-amber-500/15 text-amber-600 dark:text-amber-400",
    blue:    "bg-blue-500/15 text-blue-600 dark:text-blue-400",
  };
  return (
    <div className="flex items-center gap-2 rounded-lg px-1.5 py-1.5 transition-colors hover:bg-secondary/50">
      <span className={`inline-flex h-7 w-7 flex-none items-center justify-center rounded-md ${toneMap[tone]}`}>
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[11px] font-semibold leading-tight">{who}</p>
        <p className="truncate text-[10px] text-muted-foreground">{what}</p>
      </div>
      <span className="font-mono text-[10px] text-muted-foreground">{time}</span>
    </div>
  );
};

const SceneDashboard = () => (
  <div className="flex h-full flex-col gap-3">
    {/* Header */}
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-blue-500 text-white shadow-sm shadow-violet-500/30">
          <Activity className="h-3.5 w-3.5" />
        </span>
        <div>
          <p className="font-display text-sm font-black leading-tight tracking-tight">Operations Overview</p>
          <p className="text-[10px] text-muted-foreground">Sample Clinic · 5 clinicians · Wed Jun 10</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-[10px] font-semibold text-muted-foreground">
          <CalendarDays className="h-3 w-3" /> This week
          <ChevronDown className="h-3 w-3" />
        </span>
        <span className="inline-flex items-center gap-1 rounded-md bg-gradient-to-r from-violet-600 to-blue-600 px-2 py-1 text-[10px] font-bold text-white shadow-sm shadow-violet-600/30">
          <Sparkles className="h-3 w-3" /> AI summary
        </span>
      </div>
    </div>

    {/* 4 KPI tiles w/ sparklines */}
    <div className="grid grid-cols-4 gap-2.5">
      <KPI
        Icon={PhoneIncoming} label="Calls today" value="71" delta="+12%" tone="emerald"
        spark={[42, 51, 48, 60, 55, 68, 71]}
        sparkStroke="#a78bfa" sparkFill="rgba(167,139,250,0.2)"
      />
      <KPI
        Icon={CalendarCheck} label="Bookings" value="36" delta="+8%" tone="emerald"
        spark={[18, 22, 26, 24, 30, 33, 36]}
        sparkStroke="#10b981" sparkFill="rgba(16,185,129,0.2)"
      />
      <KPI
        Icon={DollarSign} label="Revenue" value="$12.4K" delta="+22%" tone="emerald"
        spark={[6, 7.5, 9, 8.2, 10, 11, 12.4]}
        sparkStroke="#f59e0b" sparkFill="rgba(245,158,11,0.18)"
      />
      <KPI
        Icon={Users} label="New leads" value="11" delta="-3%" tone="rose"
        spark={[14, 12, 15, 13, 12, 13, 11]}
        sparkStroke="#f43f5e" sparkFill="rgba(244,63,94,0.18)"
      />
    </div>

    {/* Chart + recent activity */}
    <div className="grid flex-1 grid-cols-12 gap-3 overflow-hidden">
      <div className="col-span-7 flex flex-col rounded-xl border border-border bg-card p-3.5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold tracking-tight">Lead → Booking funnel</p>
            <p className="text-[10px] text-muted-foreground">115 leads · 79 booked · 68.7% conversion</p>
          </div>
          <div className="flex items-center gap-2.5 text-[10px] font-semibold text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-sm bg-violet-400" /> Leads
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-sm bg-emerald-500" /> Booked
            </span>
          </div>
        </div>
        <div className="mt-2 flex-1">
          <FunnelChart />
        </div>
        {/* AI insight strip */}
        <div className="mt-2 flex items-center gap-2 rounded-lg border border-violet-500/30 bg-violet-500/8 px-2.5 py-1.5">
          <Sparkles className="h-3 w-3 flex-none text-violet-500" />
          <p className="truncate text-[11px]">
            <span className="font-bold text-violet-600 dark:text-violet-400">AI insight:</span>{" "}
            <span className="text-foreground/80">Fri pickup rate up 9% — bookings overflowed to Sat morning.</span>
          </p>
        </div>
      </div>

      <div className="col-span-5 flex flex-col rounded-xl border border-border bg-card p-3.5">
        <div className="mb-1.5 flex items-center justify-between">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
            Live activity
          </p>
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
            <span className="relative flex h-1 w-1">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-1 w-1 rounded-full bg-emerald-500" />
            </span>
            Live
          </span>
        </div>
        <div className="flex-1 space-y-0.5 overflow-hidden">
          <ActivityRow Icon={PhoneIncoming} who="Sample patient" what="AI booked · Follow-up · 3pm" time="2m" tone="emerald" />
          <ActivityRow Icon={ClipboardList} who="Sample patient" what="Intake form submitted" time="8m" tone="violet" />
          <ActivityRow Icon={Repeat} who="Reactivation flow" what="Sent to 12 dormant patients" time="16m" tone="amber" />
          <ActivityRow Icon={CheckCircle2} who="Booked into EMR" what="Sample patient · Tomorrow 3:00 PM" time="1h" tone="emerald" />
          <ActivityRow Icon={MessageSquare} who="SMS reply" what="Sample patient · &ldquo;Yes please&rdquo;" time="2h" tone="blue" />
          <ActivityRow Icon={Mail} who="Insurance request" what="Sample patient · Sun Life claim" time="3h" tone="violet" />
        </div>
      </div>
    </div>
  </div>
);

/* ============================================================================
   SCENE 2 — Call Tracker
   =========================================================================== */
const SceneCallTracker = () => {
  const calls = [
    { who: "Patient Sample 1", ai: true, dur: "1m 15s", tag: "Booked",       sub: "Follow-up",       tagColor: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" },
    { who: "Patient Sample 2", ai: true, newAx: true, dur: "3m 30s", tag: "Booked",       sub: "New Assessment",  tagColor: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" },
    { who: "Patient Sample 3", ai: true, dur: "1m 18s", tag: "General Call", sub: "",                tagColor: "bg-blue-500/15 text-blue-700 dark:text-blue-300" },
    { who: "Patient Sample 4", ai: true, dur: "1m 34s", tag: "Booked",       sub: "Follow-up",       tagColor: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" },
    { who: "Patient Sample 5", ai: true, dur: "2m 02s", tag: "Booked",       sub: "Reschedule",      tagColor: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" },
  ];

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex items-center gap-2.5 rounded-xl border border-violet-500/30 bg-violet-500/10 px-4 py-2.5">
        <Sparkles className="h-4 w-4 flex-none text-violet-500" />
        <p className="text-xs leading-snug sm:text-[13px]">
          <span className="font-semibold text-violet-600 dark:text-violet-400">
            Classification health:
          </span>{" "}
          71 calls · 97.2% pickup · 50.7% booked · 3 AI handled
        </p>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <StatTile label="Total" value="71" accent="bg-card" />
        <StatTile label="Answer rate" value="97.2%" accent="bg-emerald-500/5" />
        <StatTile label="Booked" value="36" accent="bg-emerald-500/5" sub="15 new · 21 f/up" />
        <StatTile label="Missed" value="2" accent="bg-rose-500/5" />
      </div>

      <div className="flex-1 divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
        {calls.map((c) => (
          <div key={c.who} className="flex items-center gap-3 px-4 py-3">
            <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              <PhoneIncoming className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-sm">
                <span className="truncate font-semibold">{c.who}</span>
                {c.ai && (
                  <span className="inline-flex items-center gap-0.5 rounded-md bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">
                    <Sparkles className="h-2.5 w-2.5" /> AI
                  </span>
                )}
                {c.newAx && (
                  <span className="rounded-md bg-fuchsia-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-fuchsia-700 dark:text-fuchsia-400">
                    New Ax
                  </span>
                )}
              </div>
              <div className="text-[11px] text-muted-foreground">{c.dur} · answered by AI</div>
            </div>
            <div className="flex flex-col items-end gap-0.5">
              <span className={`rounded-md px-2 py-0.5 text-[11px] font-bold ${c.tagColor}`}>
                {c.tag}
              </span>
              {c.sub && <span className="text-[10px] text-muted-foreground">{c.sub}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ============================================================================
   SCENE 3 — Scheduling
   =========================================================================== */
const CLINICIANS = [
  { name: "Dr. Avery",  booked: 10, avail: 18, dot: "bg-violet-500" },
  { name: "Dr. Bailey", booked: 23, avail:  3, dot: "bg-blue-500"   },
  { name: "Dr. Casey",  booked:  9, avail: 13, dot: "bg-emerald-500"},
  { name: "Dr. Drew",   booked: 10, avail:  4, dot: "bg-amber-500"  },
  { name: "Dr. Ellis",  booked:  7, avail: 13, dot: "bg-fuchsia-500"},
];

const SLOTS = [
  ["2:00", [
    { type: "appt", label: "Sample Pt 1", dur: "30m", src: "j" },
    { type: "appt", label: "Sample Pt 2", dur: "15m", src: "j" },
    { type: "appt", label: "Sample Pt 3", dur: "30m", src: "j" },
    { type: "book" }, { type: "book" },
  ]],
  ["2:30", [
    { type: "book" },
    { type: "appt", label: "Sample Pt 4", dur: "15m", src: "j" },
    { type: "book" }, { type: "block" }, { type: "book" },
  ]],
  ["3:15", [
    { type: "book" },
    { type: "appt", label: "Sample Pt 5", dur: "15m", src: "j" },
    { type: "book" }, { type: "block" }, { type: "book" },
  ]],
  ["3:30", [
    { type: "book" },
    { type: "appt", label: "Sample Pt 6", dur: "15m", src: "j" },
    { type: "appt", label: "Sample Pt 7", dur: "30m", src: "c", tag: "PT Assess" },
    { type: "block" }, { type: "book" },
  ]],
  ["4:00", [
    { type: "appt", label: "Sample Pt 8", dur: "15m", src: "j" },
    { type: "appt", label: "Sample Pt 9", dur: "15m", src: "j" },
    { type: "book" }, { type: "block" }, { type: "book" },
  ]],
];

const Slot = ({ slot }) => {
  if (slot.type === "book") {
    return (
      <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1.5 text-center text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
        Book
      </div>
    );
  }
  if (slot.type === "block") return <div className="rounded-md bg-secondary/40 py-3" />;
  const isCrm = slot.src === "c";
  return (
    <div className={`relative rounded-md border px-2 py-1.5 text-[11px] ${
      isCrm ? "border-violet-500/40 bg-violet-500/10" : "border-blue-500/40 bg-blue-500/10"
    }`}>
      <div className="truncate font-semibold text-foreground/85">{slot.label}</div>
      <div className="flex items-center justify-between text-[9px] text-muted-foreground">
        <span>{slot.dur}</span>
        <span className={`font-mono ${isCrm ? "text-violet-500" : "text-blue-500"}`}>
          {isCrm ? "c" : "j"}
        </span>
      </div>
      {slot.tag && (
        <div className="mt-0.5 inline-block rounded bg-violet-500/15 px-1 text-[9px] font-bold uppercase tracking-wide text-violet-600 dark:text-violet-400">
          {slot.tag}
        </div>
      )}
    </div>
  );
};

const SceneScheduling = () => (
  <div className="flex h-full flex-col gap-3">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2.5 text-sm">
        <CalendarCheck className="h-4 w-4 text-foreground/60" />
        <span className="font-bold tracking-tight">Wednesday, June 10</span>
        <span className="rounded-md bg-secondary/60 px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
          Sample Clinic
        </span>
      </div>
      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-violet-500" /> CRM
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-blue-500" /> EMR
        </span>
        <span className="inline-flex items-center gap-1 rounded-md bg-foreground px-2 py-1 font-semibold text-background">
          <Plus className="h-3 w-3" /> Add appt
        </span>
      </div>
    </div>

    <div className="grid grid-cols-5 gap-2">
      {CLINICIANS.map((c) => (
        <div key={c.name} className="rounded-lg border border-border bg-card px-2 py-2">
          <div className="flex items-center gap-1.5">
            <span className={`h-2 w-2 rounded-full ${c.dot}`} />
            <span className="truncate text-xs font-bold">{c.name}</span>
          </div>
          <div className="mt-1 flex items-center gap-1 text-[10px] text-muted-foreground">
            <span className="font-bold text-foreground/80">{c.booked}</span>
            <span>booked</span>
            <span className="font-bold text-foreground/80">· {c.avail}</span>
            <span>free</span>
          </div>
        </div>
      ))}
    </div>

    <div className="relative flex-1 overflow-hidden rounded-xl border border-border bg-card">
      <div className="divide-y divide-border">
        {SLOTS.map(([time, row], i) => (
          <div key={time} className="relative grid grid-cols-[42px_repeat(5,1fr)] gap-2 px-2.5 py-2">
            <div className="self-center text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{time}</div>
            {row.map((slot, j) => <Slot key={j} slot={slot} />)}
            {i === 2 && (
              <div className="pointer-events-none absolute -top-px left-0 right-0 flex items-center gap-1.5">
                <span className="ml-1 rounded-sm bg-rose-500 px-1.5 py-0.5 text-[9px] font-bold text-white">NOW</span>
                <span className="h-px flex-1 bg-rose-500/40" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ============================================================================
   SCENE 4 — Inbox
   =========================================================================== */
const CONTACTS = [
  { name: "Riley Carter",     t: "47m", preview: "Sounds good — see you Thursday.", active: true, initials: "RC", unread: 0, channel: "sms" },
  { name: "Avery Stone",      t: "56m", preview: "Hi! Can I move my Friday appt to…", initials: "AS", unread: 2, channel: "sms" },
  { name: "Jordan Pierce",    t: "1h",  preview: "Thanks for the reminder, confirmed.", initials: "JP", unread: 0, channel: "email" },
  { name: "Skyler Reyes",     t: "1h",  preview: "Sent insurance card · 1 PDF", initials: "SR", unread: 0, channel: "email" },
  { name: "Morgan Vale",      t: "2h",  preview: "Missed call · voicemail", initials: "MV", unread: 1, channel: "call" },
  { name: "Quinn Harper",     t: "3h",  preview: "Reactivation reply: yes please", initials: "QH", unread: 0, channel: "sms" },
];

const ChannelDot = ({ channel }) => {
  const map = {
    sms:   { bg: "bg-emerald-500", icon: MessageSquare },
    email: { bg: "bg-blue-500",    icon: Mail },
    call:  { bg: "bg-violet-500",  icon: PhoneCall },
  };
  const C = map[channel] || map.sms;
  const I = C.icon;
  return (
    <span className={`absolute -bottom-0.5 -right-0.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border-2 border-card ${C.bg} text-white`}>
      <I className="h-2 w-2" />
    </span>
  );
};

const SceneInbox = () => (
  <div className="grid h-full grid-cols-12 gap-3">
    {/* Conversation list */}
    <div className="col-span-5 hidden flex-col gap-2 sm:flex">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Inbox · 6</span>
        <span className="inline-flex items-center gap-1 rounded-md bg-gradient-to-r from-violet-600 to-blue-600 px-2 py-0.5 text-[10px] font-bold text-white shadow-sm shadow-violet-600/30">
          <Plus className="h-2.5 w-2.5" /> New
        </span>
      </div>
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
        <input
          aria-hidden tabIndex={-1} readOnly
          value="Search conversations..."
          className="block w-full rounded-md border border-border bg-card px-3 py-1.5 pl-7 text-[11px] text-muted-foreground outline-none"
        />
      </div>
      <div className="flex-1 divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
        {CONTACTS.map((c) => (
          <div
            key={c.name}
            className={`relative flex items-center gap-2.5 px-3 py-2.5 transition-colors ${
              c.active ? "bg-gradient-to-r from-violet-500/10 to-transparent" : "hover:bg-secondary/40"
            }`}
          >
            {c.active && <span className="absolute inset-y-1.5 left-0 w-0.5 rounded-full bg-gradient-to-b from-violet-500 to-blue-500" />}
            <div className="relative flex-none">
              <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full text-[10px] font-bold ${
                c.active
                  ? "bg-gradient-to-br from-violet-500 to-blue-500 text-white"
                  : "bg-secondary text-foreground/70"
              }`}>
                {c.initials}
              </span>
              <ChannelDot channel={c.channel} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between text-xs">
                <span className={`truncate ${c.active ? "font-black" : "font-bold"}`}>{c.name}</span>
                <span className={`ml-1 text-[10px] ${c.unread > 0 ? "font-bold text-foreground" : "text-muted-foreground"}`}>{c.t}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <p className="truncate text-[10px] text-muted-foreground">{c.preview}</p>
                {c.unread > 0 && (
                  <span className="inline-flex h-4 min-w-4 flex-none items-center justify-center rounded-full bg-violet-600 px-1 text-[9px] font-bold text-white">
                    {c.unread}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Thread */}
    <div className="col-span-12 flex flex-col gap-2 sm:col-span-7">
      {/* Patient header */}
      <div className="flex items-center gap-2.5 rounded-xl border border-border bg-card px-3 py-2">
        <div className="relative">
          <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 text-xs font-bold text-white">RC</span>
          <span className="absolute -bottom-0.5 -right-0.5 inline-flex h-3 w-3 rounded-full border-2 border-card bg-emerald-500" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-black tracking-tight">Riley Carter</span>
            <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">Existing</span>
            <span className="rounded bg-blue-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-blue-700 dark:text-blue-300">PT · in clinic</span>
          </div>
          <div className="text-[10px] text-muted-foreground">+1 (555) 010-4271 · riley.carter@example.com · Sample patient · 2022</div>
        </div>
        <div className="flex items-center gap-1">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border bg-card text-muted-foreground">
            <PhoneCall className="h-3 w-3" />
          </span>
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border bg-card text-muted-foreground">
            <MoreHorizontal className="h-3 w-3" />
          </span>
          <span className="inline-flex items-center gap-1 rounded-md bg-gradient-to-r from-violet-600 to-blue-600 px-2 py-1 text-[10px] font-bold text-white shadow-sm shadow-violet-600/30">
            <CalendarCheck className="h-3 w-3" /> Book
          </span>
        </div>
      </div>

      {/* Thread body */}
      <div className="flex-1 space-y-2 overflow-hidden rounded-xl border border-border bg-gradient-to-b from-secondary/20 to-transparent p-3">
        {/* Date divider */}
        <div className="flex items-center gap-2">
          <span className="h-px flex-1 bg-border" />
          <span className="rounded-full bg-secondary/70 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Tuesday · May 5</span>
          <span className="h-px flex-1 bg-border" />
        </div>

        {/* Incoming SMS */}
        <div className="flex items-end gap-2">
          <span className="inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-emerald-500/15 text-[8px] font-bold text-emerald-600 dark:text-emerald-400">RC</span>
          <div className="max-w-[68%] rounded-2xl rounded-bl-sm border border-border bg-card px-3 py-1.5">
            <p className="text-[11px] leading-snug">Hi, can I move my appointment from Friday to next Thursday morning?</p>
            <p className="mt-0.5 text-[9px] text-muted-foreground">9:02 AM · SMS</p>
          </div>
        </div>

        {/* AI suggested reply card */}
        <div className="ml-7 max-w-[68%] rounded-lg border border-violet-500/30 bg-violet-500/8 px-2.5 py-1.5">
          <div className="flex items-center gap-1 text-[10px] font-bold text-violet-600 dark:text-violet-400">
            <Sparkles className="h-2.5 w-2.5" /> AI · Suggested reply · pulled Thu 9:30 AM slot
          </div>
        </div>

        {/* Outgoing SMS */}
        <div className="flex items-end justify-end gap-2">
          <div className="max-w-[68%] rounded-2xl rounded-br-sm bg-gradient-to-br from-violet-600 to-blue-600 px-3 py-1.5 text-white shadow-sm">
            <p className="text-[11px] leading-snug">Hi Riley — yes! I have Thursday 9:30 AM with Dr. Avery. Want me to book it?</p>
            <p className="mt-0.5 flex items-center gap-1 text-[9px] text-white/80">
              9:04 AM · SMS · sent by AI <CheckCircle2 className="h-2 w-2" />
            </p>
          </div>
        </div>

        {/* Incoming SMS reply */}
        <div className="flex items-end gap-2">
          <span className="inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-emerald-500/15 text-[8px] font-bold text-emerald-600 dark:text-emerald-400">RC</span>
          <div className="max-w-[60%] rounded-2xl rounded-bl-sm border border-border bg-card px-3 py-1.5">
            <p className="text-[11px] leading-snug">Yes please. Thank you!</p>
            <p className="mt-0.5 text-[9px] text-muted-foreground">9:06 AM · SMS</p>
          </div>
        </div>

        {/* System update */}
        <div className="flex items-center justify-center gap-2 py-0.5">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-500/30 bg-blue-500/8 px-2 py-0.5 text-[10px] font-semibold text-blue-700 dark:text-blue-300">
            <RefreshCcw className="h-2.5 w-2.5" />
            Booked in EMR · Thu May 8 · 9:30 AM · Dr. Avery
          </span>
        </div>

        {/* Call card */}
        <div className="ml-7 max-w-[78%] rounded-xl border border-emerald-500/40 bg-emerald-500/8 p-2.5">
          <div className="flex items-center justify-between text-[10px]">
            <div className="flex items-center gap-1.5">
              <PhoneCall className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
              <span className="font-bold text-emerald-700 dark:text-emerald-300">Confirmation call · completed</span>
            </div>
            <span className="font-mono text-muted-foreground">0:50</span>
          </div>
          <div className="mt-1.5 flex items-center gap-2">
            <span className="inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-gradient-to-br from-violet-600 to-blue-600 text-white shadow-sm">
              <Play className="ml-0.5 h-2.5 w-2.5 fill-current" />
            </span>
            <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-secondary">
              <div className="absolute inset-y-0 left-0 w-1/3 rounded-full bg-gradient-to-r from-violet-500 to-blue-500" />
            </div>
            <span className="font-mono text-[9px] text-muted-foreground">0:18 / 0:50</span>
          </div>
          <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-violet-600 dark:text-violet-400">
            <Sparkles className="h-2.5 w-2.5" /> View AI transcript
          </div>
        </div>
      </div>

      {/* Composer */}
      <div className="rounded-xl border border-border bg-card p-2">
        <div className="mb-1.5 flex items-center gap-1 px-1 text-[10px]">
          <span className="inline-flex items-center gap-1 rounded-md bg-emerald-500/15 px-1.5 py-0.5 font-bold text-emerald-700 dark:text-emerald-300">
            <MessageSquare className="h-2.5 w-2.5" /> SMS
          </span>
          <span className="text-muted-foreground">Mail</span>
          <span className="text-muted-foreground">Internal note</span>
          <span className="ml-auto inline-flex items-center gap-1 text-violet-600 dark:text-violet-400">
            <Sparkles className="h-2.5 w-2.5" /> AI draft
          </span>
        </div>
        <div className="flex items-center gap-2">
          <input
            aria-hidden tabIndex={-1} readOnly
            value="Write a reply…"
            className="flex-1 bg-transparent text-xs text-muted-foreground outline-none"
          />
          <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
          <Smile className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="inline-flex items-center gap-1 rounded-md bg-foreground px-2 py-1 text-[10px] font-bold text-background">
            Send <Send className="h-2.5 w-2.5" />
          </span>
        </div>
      </div>
    </div>
  </div>
);

/* ============================================================================
   SCENE 5 — Form Builder (drag-and-drop)
   =========================================================================== */

const FIELD_TYPES = [
  { Icon: Type,         name: "Text"     },
  { Icon: AtSign,       name: "Email"    },
  { Icon: PhoneI,       name: "Phone"    },
  { Icon: Hash,         name: "Number"   },
  { Icon: ChevronDown,  name: "Dropdown" },
  { Icon: CalendarDays, name: "Date"     },
  { Icon: ListChecks,   name: "Choice"   },
  { Icon: Upload,       name: "File"     },
  { Icon: PenTool,      name: "Signature"},
];

const FormFieldRow = ({ label, type, placeholder, required }) => (
  <div className="group relative rounded-lg border border-border bg-card px-3 py-2.5 transition-all hover:border-violet-400/40">
    <GripVertical className="absolute -left-1 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-foreground/30 opacity-0 transition-opacity group-hover:opacity-100" />
    <div className="flex items-center justify-between">
      <p className="text-[11px] font-semibold">
        {label} {required && <span className="text-rose-500">*</span>}
      </p>
      <span className="rounded bg-secondary/60 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
        {type}
      </span>
    </div>
    {placeholder && (
      <div className="mt-1.5 rounded-md border border-border/60 bg-background px-2 py-1.5 text-[11px] text-muted-foreground/70">
        {placeholder}
      </div>
    )}
  </div>
);

const SceneFormBuilder = () => (
  <div className="grid h-full grid-cols-12 gap-3">
    {/* Field palette */}
    <div className="col-span-4 flex flex-col gap-2 rounded-xl border border-border bg-secondary/30 p-3 sm:col-span-3">
      <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
        Drag a field
      </p>
      <div className="grid grid-cols-1 gap-1.5">
        {FIELD_TYPES.map((f) => (
          <div
            key={f.name}
            className="group flex cursor-grab items-center gap-2 rounded-lg border border-border bg-card px-2.5 py-1.5 transition-all hover:border-violet-400/40 hover:shadow-sm"
          >
            <GripVertical className="h-3 w-3 text-foreground/40" />
            <f.Icon className="h-3.5 w-3.5 text-foreground/70" />
            <span className="text-[11px] font-semibold">{f.name}</span>
          </div>
        ))}
      </div>
    </div>

    {/* Form canvas */}
    <div className="col-span-8 flex flex-col gap-2.5 sm:col-span-9">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-violet-500" />
          <p className="font-display text-sm font-black tracking-tight">New Patient Intake Form</p>
        </div>
        <div className="flex items-center gap-1.5">
          <button type="button" tabIndex={-1} className="rounded-md border border-border bg-card px-2.5 py-1 text-[10px] font-bold text-foreground/80">
            Preview
          </button>
          <button type="button" tabIndex={-1} className="rounded-md bg-foreground px-2.5 py-1 text-[10px] font-bold text-background">
            Publish
          </button>
        </div>
      </div>

      <div className="flex-1 space-y-2 overflow-hidden rounded-xl border-2 border-dashed border-border bg-secondary/20 p-3">
        <FormFieldRow label="Full name"        type="Text"     placeholder="John Doe"          required />
        <FormFieldRow label="Email"            type="Email"    placeholder="you@email.com"     required />
        <FormFieldRow label="Phone"            type="Phone"    placeholder="(613) 555-0142"    required />
        <FormFieldRow label="Reason for visit" type="Dropdown" placeholder="Select an option"  required />
        <FormFieldRow label="Insurance card"   type="File"     placeholder="Upload PDF/image" />

        {/* Drop zone */}
        <div className="rounded-lg border-2 border-dashed border-violet-500/50 bg-violet-500/8 px-3 py-2.5 text-center">
          <p className="inline-flex items-center gap-1.5 text-[11px] font-bold text-violet-600 dark:text-violet-400">
            <Plus className="h-3 w-3" /> Drop a field here
          </p>
        </div>
      </div>
    </div>
  </div>
);

/* ============================================================================
   SCENE 6 — Manuals (Knowledge base + Ask Mathew AI)
   =========================================================================== */

const KB_CATEGORIES = [
  { Icon: BookOpen,      name: "Front Desk SOPs",     count: 24 },
  { Icon: FileQuestion,  name: "Cancellation & No-show", count: 8 },
  { Icon: UserCheck,     name: "New Patient Intake",  count: 12, active: true },
  { Icon: ShieldCheck,   name: "Insurance & Billing", count: 18 },
  { Icon: Receipt,       name: "EMR Cheatsheet",      count: 9  },
  { Icon: PhoneCall,     name: "Phone Scripts",       count: 15 },
];

const SceneManuals = () => (
  <div className="grid h-full grid-cols-12 gap-3">
    {/* Categories sidebar */}
    <div className="col-span-4 flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
          Knowledge base
        </p>
        <span className="font-mono text-[10px] font-bold text-muted-foreground">86 docs</span>
      </div>
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <input
          aria-hidden
          tabIndex={-1}
          readOnly
          value="Search manuals..."
          className="block w-full rounded-md border border-border bg-card px-3 py-1.5 pl-7 text-[11px] text-muted-foreground outline-none"
        />
      </div>
      <div className="flex-1 space-y-0.5 overflow-hidden">
        {KB_CATEGORIES.map((c) => (
          <div
            key={c.name}
            className={`flex items-center gap-2 rounded-lg px-2.5 py-2 transition-colors ${
              c.active ? "bg-violet-500/10 text-violet-600 dark:text-violet-400" : "hover:bg-secondary/50"
            }`}
          >
            <c.Icon className="h-3.5 w-3.5 flex-none" />
            <span className="min-w-0 flex-1 truncate text-[11px] font-semibold">{c.name}</span>
            <span className={`font-mono text-[10px] font-bold ${c.active ? "" : "text-muted-foreground"}`}>
              {c.count}
            </span>
          </div>
        ))}
      </div>
    </div>

    {/* Mathew chat */}
    <div className="col-span-8 flex flex-col gap-2.5 rounded-xl border border-border bg-card">
      <div className="flex items-center gap-2.5 border-b border-border px-3.5 py-2.5">
        <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 via-teal-500 to-emerald-500 text-white shadow-sm">
          <Bot className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 text-sm font-black tracking-tight">
            Ask Mathew
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
              <span className="relative flex h-1 w-1">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-1 w-1 rounded-full bg-emerald-500" />
              </span>
              Online
            </span>
          </p>
          <p className="text-[10px] text-muted-foreground">Your AI knowledge agent · searches 86 docs</p>
        </div>
      </div>

      <div className="flex-1 space-y-2.5 overflow-hidden px-3.5">
        {/* User question */}
        <div className="flex justify-end">
          <div className="max-w-[80%] rounded-2xl rounded-tr-sm bg-foreground px-3 py-2 text-xs text-background">
            How do I handle a no-show?
          </div>
        </div>

        {/* Mathew answer */}
        <div className="flex items-start gap-2">
          <span className="mt-0.5 inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-emerald-500 text-white">
            <Bot className="h-3 w-3" />
          </span>
          <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-secondary/50 px-3 py-2">
            <p className="text-xs font-semibold leading-snug">Our 3-step no-show protocol:</p>
            <ol className="mt-1.5 space-y-0.5 text-[11px] text-foreground/85">
              <li><span className="font-mono">1.</span> Mark in EMR as &ldquo;No-show&rdquo;</li>
              <li><span className="font-mono">2.</span> Trigger reactivation flow (SMS + Email)</li>
              <li><span className="font-mono">3.</span> Front-desk call within 24h</li>
            </ol>
            <div className="mt-2 inline-flex items-center gap-1 rounded bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-bold text-violet-600 dark:text-violet-400">
              <BookOpen className="h-2.5 w-2.5" />
              From: Cancellation &amp; No-show SOP
            </div>
          </div>
        </div>

        {/* User question 2 */}
        <div className="flex justify-end">
          <div className="max-w-[80%] rounded-2xl rounded-tr-sm bg-foreground px-3 py-2 text-xs text-background">
            What&apos;s the script for insurance billing?
          </div>
        </div>

        {/* Mathew typing */}
        <div className="flex items-start gap-2">
          <span className="mt-0.5 inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-emerald-500 text-white">
            <Bot className="h-3 w-3" />
          </span>
          <div className="rounded-2xl rounded-tl-sm bg-secondary/50 px-3.5 py-2.5">
            <span className="inline-flex gap-1">
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-foreground/40" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-foreground/40" style={{ animationDelay: "0.12s" }} />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-foreground/40" style={{ animationDelay: "0.24s" }} />
            </span>
          </div>
        </div>
      </div>

      {/* Input */}
      <div className="m-3 mt-0 flex items-center gap-2 rounded-xl border border-border bg-secondary/30 px-3 py-2">
        <Sparkles className="h-3.5 w-3.5 text-violet-500" />
        <input
          aria-hidden
          tabIndex={-1}
          readOnly
          value="Ask Mathew anything about your clinic..."
          className="flex-1 bg-transparent text-xs text-muted-foreground outline-none"
        />
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-foreground text-background">
          <Send className="h-3 w-3" />
        </span>
      </div>
    </div>
  </div>
);

/* ============================================================================
   SCENE 7 — Reactivation
   =========================================================================== */
const REACTIVATIONS = [
  { who: "Patient Sample 01", sub: "PT · Knee · Dr. Avery",      visit: "182 days ago", channel: "SMS",   chIcon: MessageSquare, status: "Reactivate", priority: "high"   },
  { who: "Patient Sample 02", sub: "Massage · Dr. Bailey",        visit: "94 days ago",  channel: "Call",  chIcon: PhoneCall,     status: "AI calling", priority: "medium" },
  { who: "Patient Sample 03", sub: "Chiro · Dr. Casey",            visit: "127 days ago", channel: "Email", chIcon: Mail,          status: "In series",  priority: "high"   },
  { who: "Patient Sample 04", sub: "PT · Shoulder · Dr. Drew",     visit: "76 days ago",  channel: "SMS",   chIcon: MessageSquare, status: "Booked",     priority: "low"    },
  { who: "Patient Sample 05", sub: "Acupuncture · Dr. Ellis",      visit: "215 days ago", channel: "Call",  chIcon: PhoneCall,     status: "Reactivate", priority: "high"   },
];

const PRIORITY_TONE = {
  high:   "bg-rose-500/15 text-rose-700 dark:text-rose-300",
  medium: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  low:    "bg-secondary text-muted-foreground",
};

const STATUS_TONE = {
  Reactivate:  "bg-violet-500/15 text-violet-600 dark:text-violet-400",
  "AI calling":"bg-amber-500/15 text-amber-700 dark:text-amber-300",
  "In series": "bg-blue-500/15 text-blue-700 dark:text-blue-300",
  Booked:      "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
};

const SceneReactivation = () => (
  <div className="flex h-full flex-col gap-3">
    {/* Header */}
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2.5">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-blue-500 text-white shadow-sm shadow-violet-500/30">
          <Repeat className="h-4 w-4" />
        </span>
        <div className="text-left">
          <p className="text-left font-display text-sm font-black leading-tight tracking-tight">Reactivation list</p>
          <p className="text-left text-[10px] text-muted-foreground">Last 30 days · win lapsed patients back automatically</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 text-[10px]">
        <span className="inline-flex h-7 items-center gap-1 rounded-md border border-border bg-card px-2 font-semibold text-muted-foreground">
          <CalendarDays className="h-3 w-3" /> Last 30 days
          <ChevronDown className="h-3 w-3" />
        </span>
        <span className="inline-flex h-7 items-center gap-1.5 rounded-md bg-gradient-to-r from-violet-600 to-blue-600 px-2.5 font-bold text-white shadow-sm shadow-violet-600/30">
          <Sparkles className="h-3 w-3" /> Bulk reactivate · 47
        </span>
      </div>
    </div>

    {/* Stat tiles */}
    <div className="grid grid-cols-4 gap-2.5">
      <StatTile label="Dormant" value="247" accent="bg-card" sub="Lapsed > 60 days" />
      <StatTile label="Outreach" value="89" accent="bg-violet-500/5" sub="Across SMS · Call · Email" />
      <StatTile label="Booked back" value="31" accent="bg-emerald-500/5" sub="13% win rate" />
      <StatTile label="Pending" value="156" accent="bg-amber-500/5" sub="Awaiting follow-up" />
    </div>

    {/* Table */}
    <div className="flex-1 overflow-hidden rounded-xl border border-border bg-card">
      <div className="grid grid-cols-12 gap-3 border-b border-border bg-secondary/40 px-3.5 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        <span className="col-span-5 text-left">Patient</span>
        <span className="col-span-2 text-left">Last visit</span>
        <span className="col-span-2 text-left">Channel</span>
        <span className="col-span-1 text-left">Priority</span>
        <span className="col-span-2 text-right">Status</span>
      </div>
      <div className="divide-y divide-border">
        {REACTIVATIONS.map((p) => (
          <div key={p.who} className="grid grid-cols-12 items-center gap-3 px-3.5 py-2.5 text-left text-xs">
            <div className="col-span-5 flex items-center gap-2.5 text-left">
              <span className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-full bg-gradient-to-br from-violet-500/80 to-blue-500/80 text-[10px] font-black text-white">
                {p.who.split(" ").map(n => n[0]).join("")}
              </span>
              <div className="min-w-0 flex-1 text-left">
                <p className="truncate text-left font-display text-[12.5px] font-bold leading-tight tracking-tight text-foreground/95">
                  {p.who}
                </p>
                <p className="truncate text-left text-[10px] leading-tight text-muted-foreground">{p.sub}</p>
              </div>
            </div>
            <div className="col-span-2 text-left">
              <span className="inline-flex items-center gap-1 font-mono text-[10.5px] text-muted-foreground">
                <Clock className="h-3 w-3" />
                {p.visit}
              </span>
            </div>
            <div className="col-span-2 inline-flex items-center gap-1.5 text-left text-foreground/80">
              <p.chIcon className="h-3 w-3" />
              <span className="text-[11px] font-semibold">{p.channel}</span>
            </div>
            <div className="col-span-1 text-left">
              <span className={`rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${PRIORITY_TONE[p.priority]}`}>
                {p.priority}
              </span>
            </div>
            <div className="col-span-2 flex justify-end">
              <span className={`rounded-md px-2 py-0.5 text-[10px] font-bold ${STATUS_TONE[p.status]}`}>
                {p.status}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ============================================================================
   SCENE 8 — Automation (workflow canvas)
   =========================================================================== */

const WfNode = ({ Icon, title, sub, tone = "default", badge, selected, style }) => {
  const toneMap = {
    default:  "border-border bg-card",
    trigger:  "border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-amber-500/5",
    ai:       "border-violet-500/40 bg-gradient-to-br from-violet-500/12 to-blue-500/6",
    action:   "border-blue-500/40 bg-gradient-to-br from-blue-500/8 to-card",
    decision: "border-fuchsia-500/40 bg-gradient-to-br from-fuchsia-500/10 to-card",
  };
  const iconBg = {
    default:  "bg-secondary/60 text-foreground/70",
    trigger:  "bg-amber-500/20 text-amber-700 dark:text-amber-300",
    ai:       "bg-gradient-to-br from-violet-500 to-blue-500 text-white",
    action:   "bg-blue-500/20 text-blue-700 dark:text-blue-300",
    decision: "bg-fuchsia-500/20 text-fuchsia-700 dark:text-fuchsia-300",
  };
  return (
    <div
      style={style}
      className={`absolute w-[180px] rounded-xl border-2 ${toneMap[tone]} px-2.5 py-2 shadow-md transition-all ${
        selected ? "ring-2 ring-violet-500/60 ring-offset-2 ring-offset-background" : ""
      }`}
    >
      <div className="flex items-start gap-2">
        <span className={`inline-flex h-7 w-7 flex-none items-center justify-center rounded-md ${iconBg[tone]}`}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <p className="truncate text-[11px] font-bold tracking-tight">{title}</p>
            {badge && (
              <span className="rounded bg-emerald-500/15 px-1 text-[8px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                {badge}
              </span>
            )}
          </div>
          {sub && <p className="mt-0.5 truncate text-[9px] text-muted-foreground">{sub}</p>}
        </div>
      </div>
    </div>
  );
};

/* ============================================================================
   SCENE 10 — Automation (classic workflow builder)
   =========================================================================== */

/* OpenAI mark — used inside the GPT step card */
const OpenAIMark = ({ className = "h-4 w-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
    <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.998-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.677l5.815 3.353-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08-4.778 2.758a.795.795 0 0 0-.393.681zm1.097-2.365 2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
  </svg>
);

/* A single workflow node — white card, icon tile, title, optional condition + footer */
const WfBlock = ({
  Icon, iconBg, iconColor, title, condition, footer, OpenAILogo, dashedAdd, highlight,
}) => {
  if (dashedAdd) {
    return (
      <div className="flex h-full w-full items-center gap-2.5 rounded-2xl border-2 border-dashed border-blue-400/60 bg-blue-50/40 px-3 py-2.5 transition-colors hover:border-blue-500 dark:border-blue-400/50 dark:bg-blue-500/10">
        <span className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-lg border border-blue-300/60 bg-white text-blue-600 shadow-sm dark:border-blue-400/30 dark:bg-blue-500/20 dark:text-blue-300">
          <Plus className="h-4 w-4" />
        </span>
        <p className="text-[13px] font-bold text-blue-600 dark:text-blue-300">Add new trigger</p>
      </div>
    );
  }
  return (
    <div className={`w-full rounded-2xl border bg-card shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${
      highlight
        ? "border-violet-500/45 ring-1 ring-violet-500/20 shadow-md shadow-violet-500/15"
        : "border-border"
    }`}>
      <div className="flex items-center gap-2.5 px-3 py-2.5">
        <span className={`inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg ${iconBg} ${iconColor} ${highlight ? "shadow-md shadow-violet-500/30" : ""}`}>
          {OpenAILogo ? <OpenAIMark className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
        </span>
        <p className="flex-1 text-[13px] font-bold leading-tight tracking-tight">{title}</p>
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground/70 transition-colors hover:bg-secondary/60 hover:text-foreground">
          <MoreHorizontal className="h-3.5 w-3.5" />
        </span>
      </div>
      {condition && (
        <div className="border-t border-border px-3 py-1.5">
          <p className="text-[11px] leading-tight text-foreground/80">{condition}</p>
        </div>
      )}
      {footer && (
        <div className="flex items-center gap-1 border-t border-border px-2.5 py-1.5 text-[10.5px] text-muted-foreground">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded transition-colors hover:bg-secondary/60 hover:text-foreground">
            <Copy className="h-3 w-3" />
          </span>
          <span className="inline-flex h-5 w-5 items-center justify-center rounded transition-colors hover:bg-rose-500/15 hover:text-rose-500">
            <Trash2 className="h-3 w-3" />
          </span>
          <span className="ml-auto inline-flex items-center gap-1 font-bold text-blue-600 dark:text-blue-400">
            <BarChart3 className="h-3 w-3" /> Stats
          </span>
        </div>
      )}
    </div>
  );
};

/* The "+" hub between steps */
const PlusHub = () => (
  <div className="my-0.5 flex justify-center" aria-hidden>
    <span className="relative inline-flex h-5 w-5 items-center justify-center rounded-full border border-dashed border-foreground/30 bg-background text-foreground/60 shadow-sm">
      <span className="absolute -top-2 h-2 w-px bg-foreground/25" />
      <Plus className="h-2.5 w-2.5" />
      <span className="absolute -bottom-2 h-2 w-px bg-foreground/25" />
    </span>
  </div>
);

const SceneAutomation = () => (
  <div className="flex h-full flex-col gap-2">
    {/* Top toolbar */}
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 text-xs">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-blue-500 text-white shadow-sm shadow-violet-500/30">
          <GitBranch className="h-3.5 w-3.5" />
        </span>
        <span className="text-muted-foreground">Automations</span>
        <ChevronRight className="h-3 w-3 text-muted-foreground/50" />
        <span className="font-display font-black tracking-tight">Call Classification v2</span>
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
          </span>
          Active
        </span>
      </div>
      <div className="flex items-center gap-1.5 text-[10px]">
        <span className="font-mono text-muted-foreground">2,148 runs · 99.4% success</span>
        <span className="inline-flex h-7 items-center gap-1 rounded-md border border-border bg-card px-2 font-bold">
          <Settings2 className="h-3 w-3" /> Settings
        </span>
        <span className="inline-flex h-7 items-center gap-1 rounded-md bg-gradient-to-r from-violet-600 to-blue-600 px-2 font-bold text-white shadow-sm shadow-violet-600/30">
          <Play className="h-2.5 w-2.5 fill-current" /> Publish
        </span>
      </div>
    </div>

    {/* Section tabs */}
    <div className="flex items-center gap-4 border-b border-border text-[11px]">
      <span className="border-b-2 border-violet-500 pb-1.5 font-bold text-violet-600 dark:text-violet-400">Builder</span>
      <span className="pb-1.5 text-muted-foreground">Settings</span>
      <span className="pb-1.5 text-muted-foreground">Enrolment history · 2,148</span>
      <span className="pb-1.5 text-muted-foreground">Execution logs</span>
      <span className="ml-auto pb-1.5 text-[10px] font-mono text-muted-foreground">v2 · edited 14m ago</span>
    </div>

    {/* Canvas */}
    <div className="relative flex-1 overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-secondary/15 to-background">
      {/* dotted grid */}
      <div
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          backgroundImage: "radial-gradient(currentColor 1px, transparent 1px)",
          backgroundSize: "16px 16px",
          color: "rgba(120,120,140,0.18)",
        }}
      />

      {/* Bracket SVG connector merging the two triggers */}
      <svg
        className="pointer-events-none absolute left-1/2 top-[112px] z-10 h-12 -translate-x-1/2"
        width="380" height="48" viewBox="0 0 380 48" aria-hidden
      >
        <path
          d="M 72 0 L 72 16 Q 72 26, 82 26 L 286 26 Q 296 26, 296 16 L 296 0"
          fill="none"
          stroke="currentColor"
          strokeOpacity="0.38"
          strokeWidth="1.5"
        />
        <line x1="184" y1="26" x2="184" y2="46" stroke="currentColor" strokeOpacity="0.38" strokeWidth="1.5" />
      </svg>

      <div className="relative z-20 mx-auto h-full max-w-2xl space-y-1 overflow-y-auto px-3 py-3 scrollbar-thin">
        {/* Top trigger row: 2 triggers + add new */}
        <div className="grid grid-cols-3 gap-2.5">
          <WfBlock
            Icon={PhoneIncoming}
            iconBg="bg-amber-100 dark:bg-amber-500/20"
            iconColor="text-amber-600 dark:text-amber-300"
            title="Call Transcript Complete"
            condition={<>Call Direction is <span className="rounded bg-secondary/70 px-1 font-mono text-[10px]">&ldquo;Outbound&rdquo;</span></>}
            footer
          />
          <WfBlock
            Icon={PhoneIncoming}
            iconBg="bg-amber-100 dark:bg-amber-500/20"
            iconColor="text-amber-600 dark:text-amber-300"
            title="Call Transcript Complete"
            condition={<>Call Direction is <span className="rounded bg-secondary/70 px-1 font-mono text-[10px]">&ldquo;Inbound&rdquo;</span></>}
            footer
          />
          <WfBlock dashedAdd />
        </div>

        {/* Spacer for the bracket SVG above */}
        <div className="h-7" aria-hidden />

        <PlusHub />

        <WfBlock
          Icon={MessageSquare}
          iconBg="bg-violet-100 dark:bg-violet-500/15"
          iconColor="text-violet-600 dark:text-violet-300"
          title="Edit Conversation"
        />
        <PlusHub />

        <WfBlock
          Icon={Clock}
          iconBg="bg-violet-100 dark:bg-violet-500/15"
          iconColor="text-violet-600 dark:text-violet-300"
          title="Wait"
        />
        <PlusHub />

        <WfBlock
          Icon={Webhook}
          iconBg="bg-blue-100 dark:bg-blue-500/15"
          iconColor="text-blue-600 dark:text-blue-300"
          title="#1 Custom Webhook"
        />
        <PlusHub />

        <WfBlock
          OpenAILogo
          iconBg="bg-gradient-to-br from-violet-500 to-blue-500"
          iconColor="text-white"
          title="#1 GPT powered by OpenAI"
          highlight
        />
        <PlusHub />

        <WfBlock
          Icon={StickyNote}
          iconBg="bg-violet-100 dark:bg-violet-500/15"
          iconColor="text-violet-600 dark:text-violet-300"
          title="Note"
        />
        <PlusHub />
      </div>
    </div>

    {/* Footer: zoom + legend */}
    <div className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-1.5 text-[10px] text-muted-foreground">
      <span className="font-mono">Zoom · 100%</span>
      <div className="flex items-center gap-1">
        <span className="inline-flex h-5 w-5 items-center justify-center rounded border border-border"><ZoomOut className="h-3 w-3" /></span>
        <span className="inline-flex h-5 w-5 items-center justify-center rounded border border-border"><ZoomIn className="h-3 w-3" /></span>
        <span className="inline-flex h-5 w-5 items-center justify-center rounded border border-border"><Maximize2 className="h-3 w-3" /></span>
      </div>
      <span className="font-mono">7 steps · 2 triggers · no code</span>
    </div>
  </div>
);

/* ============================================================================
   SCENE 9 — AI Receptionist (live call)
   =========================================================================== */

const TRANSCRIPT = [
  { who: "ai",      t: "00:02", text: "Thanks for calling Sample Physio — this is Mathew. How can I help today?" },
  { who: "caller",  t: "00:07", text: "Hi, I'd like to book a follow-up for my knee. I saw Dr. Avery a few weeks ago." },
  { who: "ai",      t: "00:14", text: "Of course. Can I grab your full name and date of birth to pull up your file?" },
  { who: "caller",  t: "00:18", text: "Sure — Sample Patient, July 12th, 1986." },
  { who: "ai",      t: "00:25", text: "Found you. Dr. Avery has Thursday at 3pm or Friday at 10am — which works better?" },
  { who: "caller",  t: "00:34", text: "Thursday 3pm is perfect." },
  { who: "ai",      t: "00:38", text: "Booked. You'll get a confirmation text in a minute. Anything else I can help with?" },
];

const VoiceWave = () => (
  <div className="flex items-end gap-0.5">
    {[8, 14, 20, 12, 22, 18, 10, 16, 24, 14, 8, 18, 12, 20].map((h, i) => (
      <span
        key={i}
        className="w-0.5 rounded-full bg-emerald-400"
        style={{
          height: `${h}px`,
          animation: `wave 1.2s ease-in-out ${i * 0.06}s infinite`,
        }}
      />
    ))}
    <style>{`@keyframes wave { 0%,100% { transform: scaleY(0.5); } 50% { transform: scaleY(1.4); } }`}</style>
  </div>
);

const SceneReceptionist = () => (
  <div className="flex h-full flex-col gap-2.5">
    {/* Top call header */}
    <div className="flex items-center gap-3 rounded-xl border border-emerald-500/40 bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent p-3">
      <div className="relative">
        <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-md shadow-emerald-500/40">
          <Headphones className="h-4 w-4" />
        </span>
        <span className="absolute -bottom-0.5 -right-0.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border-2 border-card bg-emerald-500">
          <span className="absolute h-3.5 w-3.5 animate-ping rounded-full bg-emerald-400 opacity-70" />
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p className="font-display text-sm font-black tracking-tight">Mathew · AI Receptionist</p>
          <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">Live</span>
          <span className="rounded bg-violet-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">v3.2 · Pro</span>
        </div>
        <p className="text-[10px] text-muted-foreground">
          Inbound · +1 (555) 010-4271 · Sample Patient · existing patient · 00:38
        </p>
      </div>
      <div className="flex items-center gap-2">
        <VoiceWave />
        <span className="font-mono text-[11px] font-bold text-emerald-700 dark:text-emerald-300">00:38</span>
      </div>
    </div>

    <div className="grid flex-1 grid-cols-12 gap-2.5 overflow-hidden">
      {/* Live transcript */}
      <div className="col-span-7 flex flex-col rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Live transcript</p>
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
            </span>
            Transcribing · GPT-4
          </span>
        </div>
        <div className="flex-1 space-y-1.5 overflow-hidden px-3 py-2.5">
          {TRANSCRIPT.map((m, i) => {
            const isAi = m.who === "ai";
            return (
              <div key={i} className={`flex gap-2 ${isAi ? "" : "justify-end"}`}>
                {isAi && (
                  <span className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 text-white">
                    <Bot className="h-2.5 w-2.5" />
                  </span>
                )}
                <div className={`max-w-[78%] rounded-xl px-2.5 py-1.5 text-[10.5px] leading-snug ${
                  isAi
                    ? "rounded-tl-sm border border-emerald-500/25 bg-emerald-500/8"
                    : "rounded-tr-sm border border-border bg-secondary/40"
                }`}>
                  <div className="flex items-center gap-1.5">
                    <span className={`text-[9px] font-bold uppercase tracking-wider ${isAi ? "text-emerald-700 dark:text-emerald-300" : "text-muted-foreground"}`}>
                      {isAi ? "Mathew" : "Patient"}
                    </span>
                    <span className="font-mono text-[9px] text-muted-foreground">{m.t}</span>
                  </div>
                  <p className="mt-0.5 text-foreground/85">{m.text}</p>
                </div>
                {!isAi && (
                  <span className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-secondary text-[8px] font-bold text-foreground/70">
                    SP
                  </span>
                )}
              </div>
            );
          })}
          {/* typing */}
          <div className="flex items-center gap-2">
            <span className="inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-secondary text-[8px] font-bold text-foreground/70">SP</span>
            <span className="inline-flex items-center gap-0.5 rounded-xl rounded-tl-sm border border-border bg-secondary/40 px-2.5 py-1.5">
              <span className="h-1 w-1 animate-bounce rounded-full bg-foreground/40" />
              <span className="h-1 w-1 animate-bounce rounded-full bg-foreground/40" style={{ animationDelay: "0.12s" }} />
              <span className="h-1 w-1 animate-bounce rounded-full bg-foreground/40" style={{ animationDelay: "0.24s" }} />
            </span>
          </div>
        </div>
      </div>

      {/* AI brain panel */}
      <div className="col-span-5 flex flex-col gap-2 overflow-hidden">
        {/* Intent detected */}
        <div className="rounded-xl border border-violet-500/30 bg-violet-500/8 p-2.5">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-violet-600 dark:text-violet-400">
              Intent detected
            </p>
            <span className="font-mono text-[9px] font-bold text-violet-600 dark:text-violet-400">98%</span>
          </div>
          <div className="mt-1 flex items-center gap-1.5">
            <CalendarCheck className="h-3 w-3 text-violet-600 dark:text-violet-400" />
            <p className="text-xs font-black tracking-tight">Book follow-up</p>
          </div>
          <p className="mt-0.5 text-[10px] text-muted-foreground">PT · Knee · with Dr. Avery</p>
        </div>

        {/* Sentiment + caller */}
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg border border-border bg-card p-2">
            <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Sentiment</p>
            <p className="mt-0.5 text-xs font-black text-emerald-600 dark:text-emerald-400">Positive</p>
            <p className="text-[9px] text-muted-foreground">+0.78</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-2">
            <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Caller</p>
            <p className="mt-0.5 text-xs font-black">Existing</p>
            <p className="text-[9px] text-muted-foreground">Since Mar 2024</p>
          </div>
        </div>

        {/* Suggested actions queue */}
        <div className="flex-1 overflow-hidden rounded-xl border border-border bg-card p-2.5">
          <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
            Actions queued
          </p>
          <div className="mt-1.5 space-y-1.5">
            <div className="flex items-center gap-2 rounded-md bg-emerald-500/10 px-2 py-1.5">
              <CheckCircle2 className="h-3 w-3 flex-none text-emerald-600 dark:text-emerald-400" />
              <span className="flex-1 text-[10px] font-semibold">Book Thu 3pm · Dr. Avery</span>
              <span className="font-mono text-[9px] text-emerald-700 dark:text-emerald-300">Done</span>
            </div>
            <div className="flex items-center gap-2 rounded-md border border-border bg-background px-2 py-1.5">
              <MessageSquare className="h-3 w-3 flex-none text-blue-500" />
              <span className="flex-1 text-[10px] font-semibold">Send SMS confirmation</span>
              <span className="font-mono text-[9px] text-muted-foreground">Queued</span>
            </div>
            <div className="flex items-center gap-2 rounded-md border border-border bg-background px-2 py-1.5">
              <RefreshCcw className="h-3 w-3 flex-none text-violet-500" />
              <span className="flex-1 text-[10px] font-semibold">Sync to EMR</span>
              <span className="font-mono text-[9px] text-muted-foreground">Queued</span>
            </div>
            <div className="flex items-center gap-2 rounded-md border border-border bg-background px-2 py-1.5">
              <StickyNote className="h-3 w-3 flex-none text-amber-500" />
              <span className="flex-1 text-[10px] font-semibold">Note → patient record</span>
              <span className="font-mono text-[9px] text-muted-foreground">Queued</span>
            </div>
          </div>
        </div>

        {/* Handoff controls */}
        <div className="flex items-center gap-1.5">
          <span className="inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-border bg-card py-1.5 text-[10px] font-bold">
            <Mic className="h-3 w-3" /> Whisper
          </span>
          <span className="inline-flex flex-1 items-center justify-center gap-1 rounded-md bg-gradient-to-r from-violet-600 to-blue-600 py-1.5 text-[10px] font-bold text-white shadow-sm">
            <PhoneCall className="h-3 w-3" /> Take over
          </span>
        </div>
      </div>
    </div>
  </div>
);

/* ============================================================================
   SCENE 10 — Tasks (Front Desk Tasks)
   =========================================================================== */

const TASK_KPIS = [
  { Icon: ListChecks,   label: "Total",        value: "608", tone: "text-foreground" },
  { Icon: Clock,        label: "Open",         value: "5",   tone: "text-blue-600 dark:text-blue-400" },
  { Icon: AlertCircle,  label: "High priority",value: "0",   tone: "text-rose-500" },
  { Icon: ShieldAlert,  label: "Overdue",      value: "1",   tone: "text-amber-600 dark:text-amber-400" },
  { Icon: CheckCircle2, label: "Completed",    value: "603", tone: "text-emerald-600 dark:text-emerald-400" },
];

const TASKS = [
  { src: "AI Lead",     title: "New General Call — Sample A",
    sub: "We were unable to identify the request on the AI conversation transcript.",
    contact: { name: "Patient Sample A",  email: "patient.a@example.com",  phone: "+1 555 010 6298" },
    team: "Assign", user: "Front Desk · Glebe", priority: "Medium", due: "Jun 16", status: "Open", overdue: false },
  { src: "Form",        title: "Initial Screening — Sample B | Contact Form",
    sub: "Please email/sms the customer and try to book an appointment.",
    contact: { name: "Patient Sample B",   email: "patient.b@example.com",  phone: "+1 555 010 4757" },
    team: "Assign", user: "Front Desk · Glebe", priority: "Medium", due: "Jun 16", status: "Open", overdue: false },
  { src: "AI Lead",     title: "Initial Screening — Sample C",
    sub: "Please email/sms the customer and try to book an appointment.",
    contact: { name: "Patient Sample C",   email: "patient.c@example.com",  phone: "+1 555 010 0967" },
    team: "Assign", user: "Front Desk · Glebe", priority: "Medium", due: "Jun 16", status: "Open", overdue: false },
  { src: "AI Lead",     title: "New Booked — Sample D",
    sub: "AI booked — confirm callback within 24 hours.",
    contact: { name: "Patient Sample D",   email: "patient.d@example.com",  phone: "+1 555 010 9069" },
    team: "Assign", user: "Front Desk · Glebe", priority: "Medium", due: "Jun 12", status: "Open", overdue: true },
];

const PRIORITY_PILL = {
  Medium: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
  High:   "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30",
  Low:    "bg-secondary/70 text-muted-foreground border-border",
};

const TaskKpiTile = ({ Icon, label, value, tone }) => (
  <div className="flex items-center justify-between rounded-xl border border-border bg-card px-3 py-2">
    <div>
      <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
      <p className={`font-display text-xl font-black tracking-[-0.02em] ${tone}`}>{value}</p>
    </div>
    <Icon className={`h-4 w-4 ${tone}`} />
  </div>
);

const SceneTasks = () => (
  <div className="flex h-full flex-col gap-2.5">
    {/* Header */}
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-violet-500 text-white shadow-sm shadow-blue-500/30">
          <ClipboardList className="h-3.5 w-3.5" />
        </span>
        <div>
          <p className="font-display text-sm font-black leading-tight tracking-tight">Front Desk Tasks</p>
          <p className="text-[10px] text-muted-foreground">Sample Clinic · 5 open · 1 overdue</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-[10px] font-semibold text-muted-foreground">
          <RefreshCcw className="h-3 w-3" /> Quick sync
        </span>
        <span className="inline-flex items-center gap-1 rounded-md bg-gradient-to-r from-violet-600 to-blue-600 px-2 py-1 text-[10px] font-bold text-white shadow-sm shadow-violet-600/30">
          <Plus className="h-3 w-3" /> New task
        </span>
      </div>
    </div>

    {/* KPI strip */}
    <div className="grid grid-cols-5 gap-2">
      {TASK_KPIS.map((k) => <TaskKpiTile key={k.label} {...k} />)}
    </div>

    {/* Filter row */}
    <div className="flex items-center gap-1.5 rounded-xl border border-border bg-card px-2 py-1.5">
      <div className="relative flex-1">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
        <input
          aria-hidden tabIndex={-1} readOnly
          value="Search tasks, contacts, email, phone..."
          className="block w-full bg-transparent pl-6 text-[11px] text-muted-foreground outline-none"
        />
      </div>
      {["Open", "All FD Teams", "All Users", "All Priority", "All Sources"].map((f) => (
        <span key={f} className="inline-flex flex-none items-center gap-1 rounded-md border border-border bg-background px-1.5 py-1 text-[10px] font-semibold">
          {f}
          <ChevronDown className="h-2.5 w-2.5 text-muted-foreground" />
        </span>
      ))}
      <span className="font-mono text-[9px] text-muted-foreground">Synced 19m ago</span>
    </div>

    {/* Task table */}
    <div className="flex-1 overflow-hidden rounded-xl border border-border bg-card">
      {/* Column headers */}
      <div className="grid grid-cols-12 gap-2 border-b border-border bg-secondary/40 px-3 py-1.5 text-[9px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
        <span className="col-span-1">Done</span>
        <span className="col-span-4">Task</span>
        <span className="col-span-2">Contact</span>
        <span className="col-span-1">Team</span>
        <span className="col-span-1">User</span>
        <span className="col-span-1">Priority</span>
        <span className="col-span-1">Due</span>
        <span className="col-span-1 text-right">Status</span>
      </div>
      <div className="divide-y divide-border">
        {TASKS.map((t, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 text-[11px]">
            <div className="col-span-1 flex items-start">
              <span className="inline-flex h-5 items-center gap-1 rounded-md border border-border bg-background px-1.5 text-[9px] font-bold text-muted-foreground">
                <CheckCircle2 className="h-2.5 w-2.5" /> Mark
              </span>
            </div>
            <div className="col-span-4 min-w-0">
              <div className="flex items-center gap-1.5">
                <ChevronDown className="h-3 w-3 text-muted-foreground" />
                <p className="truncate font-bold">
                  <span className="text-violet-600 dark:text-violet-400">({t.src})</span>{" "}
                  {t.title.replace(`${t.src} `, "")}
                </p>
              </div>
              <p className="ml-4 mt-0.5 truncate text-[10px] text-muted-foreground">{t.sub}</p>
            </div>
            <div className="col-span-2 min-w-0">
              <p className="truncate font-bold text-blue-600 dark:text-blue-400">{t.contact.name}</p>
              <p className="truncate text-[9px] text-muted-foreground">
                <Mail className="mr-0.5 inline h-2 w-2" />
                {t.contact.email}
              </p>
              <p className="truncate text-[9px] text-muted-foreground">
                <PhoneI className="mr-0.5 inline h-2 w-2" />
                {t.contact.phone}
              </p>
            </div>
            <div className="col-span-1">
              <span className="inline-flex items-center gap-0.5 rounded-md border border-dashed border-rose-300/60 bg-rose-50/40 px-1.5 py-0.5 text-[9px] font-semibold text-rose-500 dark:border-rose-500/30 dark:bg-rose-500/8 dark:text-rose-400">
                {t.team} <ChevronDown className="h-2 w-2" />
              </span>
            </div>
            <div className="col-span-1">
              <span className="inline-flex items-center gap-0.5 rounded-md border border-border bg-background px-1.5 py-0.5 text-[9px] font-semibold">
                {t.user} <ChevronDown className="h-2 w-2" />
              </span>
            </div>
            <div className="col-span-1">
              <span className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${PRIORITY_PILL[t.priority]}`}>
                {t.priority}
              </span>
            </div>
            <div className="col-span-1 text-[10px]">
              {t.overdue ? (
                <span className="inline-flex items-center gap-0.5 font-bold text-rose-500">
                  {t.due} <AlertCircle className="h-2.5 w-2.5" />
                </span>
              ) : (
                <span className="text-muted-foreground">{t.due}</span>
              )}
            </div>
            <div className="col-span-1 flex justify-end">
              <span className="inline-flex items-center rounded-md border border-blue-500/30 bg-blue-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-blue-700 dark:text-blue-300">
                {t.status}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ============================================================================
   SCENE — Patient profile (360° view)
   =========================================================================== */

const InfoRow = ({ Icon, label, value, hint }) => (
  <div className="flex items-start gap-2.5 py-2 text-left">
    <span className="mt-0.5 inline-flex h-6 w-6 flex-none items-center justify-center rounded-md bg-secondary/60 text-foreground/65">
      <Icon className="h-3 w-3" />
    </span>
    <div className="min-w-0 flex-1 text-left">
      <p className="text-left text-[9.5px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
      <p className="truncate text-left font-display text-[12.5px] font-bold leading-tight tracking-tight text-foreground/95">{value}</p>
      {hint && <p className="truncate text-left text-[10px] leading-tight text-muted-foreground">{hint}</p>}
    </div>
  </div>
);

const PatientStatTile = ({ label, value, sub, tone = "" }) => (
  <div className="rounded-lg border border-border bg-card px-2.5 py-2 text-left">
    <p className="text-[9.5px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
    <p className={`mt-1 font-display text-[16px] font-black leading-none tracking-[-0.02em] ${tone}`}>{value}</p>
    {sub && <p className="mt-1 text-[10px] leading-tight text-muted-foreground">{sub}</p>}
  </div>
);

const ScenePatient = () => (
  <div className="flex h-full flex-col gap-4">
    {/* Hero header — clean, breathing room */}
    <div className="flex items-center gap-3.5 rounded-2xl border border-border bg-gradient-to-br from-card to-secondary/15 p-4">
      <div className="relative">
        <span className="inline-flex h-14 w-14 flex-none items-center justify-center rounded-full bg-gradient-to-br from-violet-500 via-blue-500 to-emerald-500 font-display text-base font-black text-white shadow-md shadow-violet-500/30">
          RC
        </span>
        <span className="absolute -bottom-0.5 -right-0.5 inline-flex h-3.5 w-3.5 rounded-full border-2 border-card bg-emerald-500" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <p className="font-display text-lg font-black tracking-tight">Riley Carter</p>
          <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">Active</span>
          <span className="rounded bg-violet-500/15 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">VIP</span>
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">
          Female · 38 · Patient since Mar 2022 · <span className="font-mono text-foreground/60">#PT-0427</span>
        </p>
      </div>
      <div className="flex flex-none items-center gap-1.5">
        <span className="inline-flex h-8 items-center gap-1 rounded-lg border border-border bg-card px-2.5 text-[10.5px] font-bold">
          <MessageSquare className="h-3 w-3" /> Message
        </span>
        <span className="inline-flex h-8 items-center gap-1 rounded-lg bg-gradient-to-r from-violet-600 to-blue-600 px-2.5 text-[10.5px] font-bold text-white shadow-sm shadow-violet-600/30">
          <CalendarCheck className="h-3 w-3" /> Book
        </span>
      </div>
    </div>

    {/* Sub-tabs — simpler */}
    <div className="flex items-center gap-5 border-b border-border text-[11.5px]">
      <span className="border-b-2 border-violet-500 pb-2 font-display font-black tracking-tight text-violet-600 dark:text-violet-400">Overview</span>
      <span className="pb-2 font-medium text-muted-foreground">Appointments</span>
      <span className="pb-2 font-medium text-muted-foreground">Notes</span>
      <span className="pb-2 font-medium text-muted-foreground">Activity</span>
      <span className="pb-2 font-medium text-muted-foreground">Billing</span>
    </div>

    {/* 3 hero stat tiles — generous spacing */}
    <div className="grid grid-cols-3 gap-3">
      <div className="rounded-2xl border border-border bg-card p-4">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Lifetime visits</p>
        <p className="mt-2 font-display text-3xl font-black tracking-[-0.02em]">24</p>
        <p className="mt-1 text-[11px] text-muted-foreground">Last visit · 3d ago</p>
      </div>
      <div className="rounded-2xl border border-border bg-card p-4">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Next appointment</p>
        <p className="mt-2 font-display text-3xl font-black tracking-[-0.02em] text-violet-600 dark:text-violet-400">Thu</p>
        <p className="mt-1 text-[11px] text-muted-foreground">3:00 PM · Dr. Avery</p>
      </div>
      <div className="rounded-2xl border border-border bg-card p-4">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Lifetime value</p>
        <p className="mt-2 font-display text-3xl font-black tracking-[-0.02em] text-emerald-600 dark:text-emerald-400">$4.2K</p>
        <p className="mt-1 text-[11px] text-muted-foreground">0 outstanding</p>
      </div>
    </div>

    {/* Main 2-col area with breathing room — Activity (real) + Notes (placeholder/blur) */}
    <div className="grid flex-1 grid-cols-12 gap-3 overflow-hidden">
      {/* LEFT — Recent activity (clean, only 3 items) */}
      <div className="col-span-7 rounded-2xl border border-border bg-card p-4">
        <div className="flex items-center justify-between">
          <p className="font-display text-[12px] font-black uppercase tracking-[0.18em]">Recent activity</p>
          <span className="text-[10.5px] font-bold text-violet-600 dark:text-violet-400">View all</span>
        </div>
        <div className="mt-3.5 space-y-3">
          {[
            { Icon: CalendarCheck, title: "AI booked Thursday 3:00 PM",        sub: "with Dr. Avery · synced to EMR",       time: "2 min ago", tone: "emerald" },
            { Icon: MessageSquare, title: "Replied to SMS reminder",          sub: "\u201cYes please. Thank you.\u201d",   time: "1 hr ago",  tone: "blue"    },
            { Icon: CheckCircle2,  title: "Visit completed",                  sub: "PT Follow-up · 45 min · Dr. Avery",     time: "3 days ago", tone: "violet"  },
          ].map((r, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className={`mt-0.5 inline-flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-${r.tone}-500/15 text-${r.tone}-600 dark:text-${r.tone}-400`}>
                <r.Icon className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-left font-display text-[13px] font-bold tracking-tight">{r.title}</p>
                <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{r.sub}</p>
              </div>
              <span className="font-mono text-[10px] text-muted-foreground">{r.time}</span>
            </div>
          ))}
        </div>

        {/* Blurred "more activity" placeholder */}
        <div className="relative mt-4 select-none overflow-hidden rounded-xl border border-dashed border-border/70 bg-secondary/20 p-3">
          <div className="space-y-2 opacity-30 blur-[2px]">
            <div className="flex items-center gap-2">
              <span className="h-7 w-7 flex-none rounded-lg bg-foreground/15" />
              <div className="flex-1 space-y-1">
                <span className="block h-2 w-2/3 rounded-full bg-foreground/15" />
                <span className="block h-1.5 w-1/2 rounded-full bg-foreground/10" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-7 w-7 flex-none rounded-lg bg-foreground/15" />
              <div className="flex-1 space-y-1">
                <span className="block h-2 w-3/4 rounded-full bg-foreground/15" />
                <span className="block h-1.5 w-2/5 rounded-full bg-foreground/10" />
              </div>
            </div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="rounded-full border border-border bg-card/95 px-3 py-1 text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground backdrop-blur">
              + 47 more events
            </span>
          </div>
        </div>
      </div>

      {/* RIGHT — Patient details summary (clean key facts only + AI snapshot) */}
      <div className="col-span-5 flex flex-col gap-3">
        <div className="rounded-2xl border border-border bg-card p-4">
          <p className="font-display text-[12px] font-black uppercase tracking-[0.18em]">About</p>
          <div className="mt-3 space-y-2.5">
            <div className="flex items-center gap-2.5">
              <PhoneI className="h-3.5 w-3.5 flex-none text-muted-foreground" />
              <span className="text-[12px] font-medium">+1 (555) 010-4271</span>
            </div>
            <div className="flex items-center gap-2.5">
              <Mail className="h-3.5 w-3.5 flex-none text-muted-foreground" />
              <span className="truncate text-[12px] font-medium">riley.carter@example.com</span>
            </div>
            <div className="flex items-center gap-2.5">
              <ShieldCheck className="h-3.5 w-3.5 flex-none text-muted-foreground" />
              <span className="text-[12px] font-medium">Sun Life Financial</span>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-violet-500/30 bg-gradient-to-br from-violet-500/10 to-blue-500/5 p-4">
          <p className="flex items-center gap-1.5 font-display text-[11px] font-black uppercase tracking-[0.18em] text-violet-600 dark:text-violet-400">
            <Sparkles className="h-3 w-3" /> AI snapshot
          </p>
          <p className="mt-2 text-[12px] leading-relaxed text-foreground/90">
            Returning patient · 4 visits in the last 90 days · responds best to SMS reminders 24h ahead.
          </p>
        </div>
      </div>
    </div>
  </div>
);

/* ============================================================================
   SCENE — Campaign (email + SMS marketing)
   =========================================================================== */
const SceneCampaign = () => (
  <div className="flex h-full flex-col gap-3">
    {/* Header */}
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2.5">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-rose-500 to-orange-500 text-white shadow-md shadow-rose-500/30">
          <Megaphone className="h-4 w-4" />
        </span>
        <div>
          <p className="font-display text-sm font-black leading-tight tracking-tight">Campaign Manager</p>
          <p className="text-[10px] text-muted-foreground">Email &amp; SMS · 12 active · 4 scheduled</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 text-[10px]">
        <span className="inline-flex h-7 items-center gap-1 rounded-md border border-border bg-card px-2 font-bold">
          <CalendarDays className="h-3 w-3" /> Last 30d
          <ChevronDown className="h-3 w-3" />
        </span>
        <span className="inline-flex h-7 items-center gap-1 rounded-md bg-gradient-to-r from-rose-600 to-orange-600 px-2.5 font-bold text-white shadow-sm shadow-rose-600/30">
          <Plus className="h-3 w-3" /> New campaign
        </span>
      </div>
    </div>

    {/* 4 KPI tiles */}
    <div className="grid grid-cols-4 gap-2">
      <div className="rounded-xl border border-border bg-card px-3 py-2">
        <p className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Sent</p>
        <p className="mt-1 font-display text-xl font-black tracking-[-0.02em]">4,820</p>
        <p className="text-[9.5px] text-muted-foreground">+18% wow</p>
      </div>
      <div className="rounded-xl border border-border bg-card px-3 py-2">
        <p className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Open rate</p>
        <p className="mt-1 font-display text-xl font-black tracking-[-0.02em] text-emerald-600 dark:text-emerald-400">42%</p>
        <p className="text-[9.5px] text-muted-foreground">Above industry 31%</p>
      </div>
      <div className="rounded-xl border border-border bg-card px-3 py-2">
        <p className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Click rate</p>
        <p className="mt-1 font-display text-xl font-black tracking-[-0.02em] text-violet-600 dark:text-violet-400">14.6%</p>
        <p className="text-[9.5px] text-muted-foreground">3.2× industry avg</p>
      </div>
      <div className="rounded-xl border border-border bg-card px-3 py-2">
        <p className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Booked</p>
        <p className="mt-1 font-display text-xl font-black tracking-[-0.02em] text-amber-600 dark:text-amber-400">87</p>
        <p className="text-[9.5px] text-muted-foreground">From campaigns</p>
      </div>
    </div>

    {/* Body: list + preview */}
    <div className="grid flex-1 grid-cols-12 gap-3 overflow-hidden">
      {/* Campaign list */}
      <div className="col-span-7 overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Recent campaigns</p>
          <span className="font-mono text-[10px] text-muted-foreground">12 active</span>
        </div>
        <div className="divide-y divide-border text-[11px]">
          {[
            { name: "Spring Tune-up · PT reactivation",     ch: "Email", chIcon: Mail,          status: "Active",    audience: "247 dormant",    sent: "2,140", open: "44%", booked: 23, tone: "emerald" },
            { name: "May intake form follow-up",             ch: "SMS",   chIcon: MessageSquare, status: "Active",    audience: "84 new leads",   sent: "84",    open: "62%", booked: 19, tone: "emerald" },
            { name: "Insurance reminder · annual benefits",  ch: "Email", chIcon: Mail,          status: "Scheduled", audience: "1,420 active",   sent: "—",     open: "—",   booked: 0,  tone: "amber"   },
            { name: "Massage promo · weekend slots",         ch: "SMS",   chIcon: MessageSquare, status: "Draft",     audience: "342 segment",    sent: "—",     open: "—",   booked: 0,  tone: "blue"    },
            { name: "Win-back · 90-day dormant",             ch: "Email", chIcon: Mail,          status: "Sent",      audience: "168 dormant",    sent: "168",   open: "38%", booked: 12, tone: "violet"  },
          ].map((c, i) => (
            <div key={i} className="grid grid-cols-12 items-center gap-2 px-3 py-2">
              <div className="col-span-5 flex items-center gap-2 min-w-0">
                <span className="inline-flex h-7 w-7 flex-none items-center justify-center rounded-md bg-secondary/60 text-foreground/70">
                  <c.chIcon className="h-3 w-3" />
                </span>
                <div className="min-w-0">
                  <p className="truncate font-display font-black tracking-tight">{c.name}</p>
                  <p className="truncate text-[9.5px] text-muted-foreground">{c.audience}</p>
                </div>
              </div>
              <span className="col-span-2 font-mono text-[10px] text-muted-foreground">{c.sent} sent</span>
              <span className="col-span-2 font-mono text-[10px] text-emerald-600 dark:text-emerald-400">{c.open} open</span>
              <span className="col-span-1 text-right font-mono text-[10px] font-bold">+{c.booked}</span>
              <span className={`col-span-2 inline-flex justify-end`}>
                <span className={`rounded-md px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wider bg-${c.tone}-500/15 text-${c.tone}-700 dark:text-${c.tone}-300`}>{c.status}</span>
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Campaign preview */}
      <div className="col-span-5 overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Preview · Spring Tune-up</p>
          <Eye className="h-3 w-3 text-muted-foreground" />
        </div>
        <div className="p-3">
          {/* Mock email card */}
          <div className="overflow-hidden rounded-lg border border-border">
            <div className="bg-gradient-to-r from-rose-500/15 to-orange-500/10 px-3 py-2">
              <p className="text-[9.5px] font-bold uppercase tracking-wider text-rose-600 dark:text-rose-400">From: hello@sample-clinic.com</p>
              <p className="mt-0.5 text-[12px] font-display font-black tracking-tight">Time for your spring tune-up, {`{{ first_name }}`}?</p>
            </div>
            <div className="p-3 text-[11px] leading-relaxed text-foreground/85">
              <p>Hi <span className="rounded bg-secondary/70 px-1 font-mono text-[10px]">{`{{ first_name }}`}</span>,</p>
              <p className="mt-1.5">It&apos;s been a while since we saw you for your knee. Want me to book your follow-up with <span className="font-bold">Dr. Avery</span>?</p>
              <span className="mt-2.5 inline-block rounded-md bg-gradient-to-r from-rose-600 to-orange-600 px-3 py-1.5 text-[10px] font-bold text-white shadow-sm">
                Book my visit →
              </span>
            </div>
          </div>

          {/* Schedule meta */}
          <div className="mt-3 grid grid-cols-2 gap-2 text-[10.5px]">
            <div className="rounded-md border border-border bg-background px-2 py-1.5">
              <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Send</p>
              <p className="mt-0.5 font-mono font-bold">Tue · 9:00 AM</p>
            </div>
            <div className="rounded-md border border-border bg-background px-2 py-1.5">
              <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Audience</p>
              <p className="mt-0.5 font-mono font-bold">247 patients</p>
            </div>
          </div>

          {/* AI hint */}
          <div className="mt-3 rounded-md border border-violet-500/30 bg-violet-500/8 px-2.5 py-1.5">
            <p className="flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-wider text-violet-600 dark:text-violet-400">
              <Sparkles className="h-2.5 w-2.5" /> AI suggestion
            </p>
            <p className="mt-0.5 text-[10.5px] leading-snug text-foreground/85">
              Send 9 AM Tuesday — your audience opens 38% more than other slots.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* ============================================================================
   SCENE — Classroom (online training + certificates)
   =========================================================================== */
const SceneClassroom = () => (
  <div className="flex h-full flex-col gap-3">
    {/* Header */}
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2.5">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-500 to-teal-500 text-white shadow-md shadow-cyan-500/30">
          <GraduationCap className="h-4 w-4" />
        </span>
        <div>
          <p className="font-display text-sm font-black leading-tight tracking-tight">Clinic Academy</p>
          <p className="text-[10px] text-muted-foreground">Online training · certified · 14 staff enrolled</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 text-[10px]">
        <span className="inline-flex h-7 items-center gap-1 rounded-md border border-border bg-card px-2 font-semibold text-muted-foreground">
          <Award className="h-3 w-3" /> 8 certs issued
        </span>
        <span className="inline-flex h-7 items-center gap-1 rounded-md bg-gradient-to-r from-cyan-600 to-teal-600 px-2.5 font-bold text-white shadow-sm shadow-cyan-600/30">
          <Plus className="h-3 w-3" /> Assign course
        </span>
      </div>
    </div>

    {/* 4 KPI tiles */}
    <div className="grid grid-cols-4 gap-2">
      <div className="rounded-xl border border-border bg-card px-3 py-2">
        <p className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Courses</p>
        <p className="mt-1 font-display text-xl font-black tracking-[-0.02em]">12</p>
        <p className="text-[9.5px] text-muted-foreground">4 in progress</p>
      </div>
      <div className="rounded-xl border border-border bg-card px-3 py-2">
        <p className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Completion</p>
        <p className="mt-1 font-display text-xl font-black tracking-[-0.02em] text-emerald-600 dark:text-emerald-400">94%</p>
        <p className="text-[9.5px] text-muted-foreground">Past 90 days</p>
      </div>
      <div className="rounded-xl border border-border bg-card px-3 py-2">
        <p className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Certificates</p>
        <p className="mt-1 font-display text-xl font-black tracking-[-0.02em] text-amber-600 dark:text-amber-400">8</p>
        <p className="text-[9.5px] text-muted-foreground">Issued this year</p>
      </div>
      <div className="rounded-xl border border-border bg-card px-3 py-2">
        <p className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Avg score</p>
        <p className="mt-1 font-display text-xl font-black tracking-[-0.02em] text-violet-600 dark:text-violet-400">92</p>
        <p className="text-[9.5px] text-muted-foreground">/ 100</p>
      </div>
    </div>

    {/* Course grid */}
    <div className="grid grid-cols-2 gap-2.5">
      {[
        { name: "AI Receptionist 101",      sub: "Phone scripts · call routing",     progress: 100, students: 6, status: "Complete",   tone: "emerald", Icon: Headphones, gradient: "from-emerald-500/15 to-teal-500/10" },
        { name: "Patient Communication",    sub: "Empathy · de-escalation · email",  progress: 72,  students: 4, status: "In progress", tone: "violet",  Icon: MessageSquare, gradient: "from-violet-500/15 to-blue-500/10" },
        { name: "Insurance & Billing",      sub: "Claims · explanations · disputes", progress: 45,  students: 3, status: "In progress", tone: "amber",   Icon: ShieldCheck, gradient: "from-amber-500/15 to-orange-500/10" },
        { name: "HIPAA & Compliance",       sub: "Privacy · data handling · audits", progress: 0,   students: 14, status: "Not started", tone: "rose",    Icon: ShieldAlert, gradient: "from-rose-500/15 to-pink-500/10" },
      ].map((c, i) => (
        <div key={i} className="overflow-hidden rounded-xl border border-border bg-card transition-shadow hover:shadow-md">
          <div className={`flex items-center justify-between bg-gradient-to-r ${c.gradient} px-3 py-2.5`}>
            <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg bg-card/80 text-${c.tone}-600 dark:text-${c.tone}-400 backdrop-blur ring-1 ring-inset ring-white/10`}>
              <c.Icon className="h-4 w-4" />
            </span>
            <span className={`rounded-md bg-card/80 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-${c.tone}-700 dark:text-${c.tone}-300 backdrop-blur`}>{c.status}</span>
          </div>
          <div className="p-3">
            <p className="font-display text-[13px] font-black tracking-tight">{c.name}</p>
            <p className="mt-0.5 text-[10.5px] text-muted-foreground">{c.sub}</p>
            <div className="mt-2.5">
              <div className="flex items-center justify-between text-[9.5px]">
                <span className="font-bold text-muted-foreground">{c.progress}%</span>
                <span className="text-muted-foreground">{c.students} enrolled</span>
              </div>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-secondary">
                <div className={`h-full rounded-full bg-gradient-to-r from-${c.tone}-500 to-${c.tone}-400`} style={{ width: `${c.progress}%` }} />
              </div>
            </div>
            {c.progress === 100 && (
              <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
                <Award className="h-2.5 w-2.5" /> Certificate issued
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* ============================================================================
   PRODUCT MOCKUP — window chrome + cycling scenes
   =========================================================================== */
const SCENES = [
  { id: "dashboard",   label: "Dashboard",      hook: "Your clinic's pulse, in one screen",                Component: SceneDashboard,    narration: "Calls, bookings, revenue and live activity — your clinic's pulse in one screen." },
  { id: "receptionist",label: "AI Receptionist",hook: "Watch Mathew answer & book a call live",            Component: SceneReceptionist, narration: "Mathew, your AI receptionist, handles inbound calls — detects intent, books the slot, syncs to your EMR." },
  { id: "calltracker", label: "Call Tracker",   hook: "Every call · classified · audited",                 Component: SceneCallTracker,  narration: "Every call classified — booked vs general — with sentiment, transcript and the audit trail." },
  { id: "scheduling",  label: "Scheduling",     hook: "Calendar in sync with your EMR — both ways",        Component: SceneScheduling,   narration: "A calendar that stays in two-way sync with your EMR — book, move, cancel from either side." },
  { id: "patient",     label: "Patient",        hook: "A 360° view of every patient — one screen",         Component: ScenePatient,      narration: "A 360° patient view — appointments, tasks, notes, activity and an AI snapshot in one screen." },
  { id: "inbox",       label: "Inbox",          hook: "Calls, SMS, email & social on one thread",          Component: SceneInbox,        narration: "Calls, SMS, email and DMs land in one inbox — with intent tags and AI-drafted replies." },
  { id: "tasks",       label: "Tasks",          hook: "Front desk tasks, routed by AI",                    Component: SceneTasks,        narration: "Front-desk tasks routed automatically — AI tags priority, owner and due date the moment a lead lands." },
  { id: "campaign",    label: "Campaign",       hook: "Email & SMS campaigns that book patients",          Component: SceneCampaign,     narration: "Email and SMS campaigns with audience targeting and AI-picked send times — built for clinics." },
  { id: "classroom",   label: "Classroom",      hook: "Online training & certificates for your team",      Component: SceneClassroom,    narration: "Clinic Academy — online training courses for your team with progress tracking and certificates." },
  { id: "manuals",     label: "Manuals",        hook: "Ask Mathew — your AI knowledge agent",              Component: SceneManuals,      narration: "Ask Mathew — your team's knowledge agent — answering staff questions from your own SOPs and manuals." },
  { id: "reactivation",label: "Reactivation",   hook: "Win back lapsed patients — automated",              Component: SceneReactivation, narration: "Reactivation that runs itself — dormant patients picked, sequenced and booked without lifting a finger." },
];

const SCENE_CAPTION = {
  dashboard:    "Dashboard · the pulse of your clinic in one place",
  receptionist: "AI Receptionist · live calls, intent and booking — fully autonomous",
  calltracker:  "Every call · classified · booked · audited",
  scheduling:   "Calendar · 2-way sync with your EMR · CRM bookings live",
  patient:      "Patient profile · overview, appointments, notes & activity",
  inbox:        "Conversations · calls, SMS, email & social on one thread",
  tasks:        "Front Desk Tasks · what to action next · routed by AI",
  campaign:     "Campaigns · email & SMS · recall, win-back, promo — in one builder",
  classroom:    "Clinic Academy · online courses with certificates for your team",
  manuals:      "Ask Mathew · your AI knowledge agent · 86 docs and counting",
  reactivation: "Reactivation · win lapsed patients back · AI-prioritized",
};

export const ProductMockup = () => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [spotIdx, setSpotIdx] = useState(1);

  // Auto-cycle scenes
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % SCENES.length), 11000);
    return () => clearInterval(t);
  }, [paused]);

  // Auto-cycle spotlight on a *different* tab — invites clicks
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => {
      setSpotIdx((prev) => {
        let n = (prev + 1) % SCENES.length;
        if (n === idx) n = (n + 1) % SCENES.length;
        return n;
      });
    }, 3200);
    return () => clearInterval(t);
  }, [paused, idx]);

  const active = SCENES[idx];
  const Scene = active.Component;
  const safeSpotIdx = spotIdx === idx ? (idx + 1) % SCENES.length : spotIdx;
  const spotScene = SCENES[safeSpotIdx];

  const onSelectScene = (i) => {
    setIdx(i);
    setPaused(true);
  };

  return (
    <div className="relative mx-auto w-full max-w-6xl" data-testid="product-mockup">
      {/* Smart spotlight hint — animates between tab pitches */}
      <div className="mb-4 flex h-9 items-center justify-center" data-testid="mockup-hint">
        <AnimatePresence mode="wait">
          {!paused ? (
            <motion.button
              key={spotScene.id}
              type="button"
              onClick={() => onSelectScene(safeSpotIdx)}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.35 }}
              data-testid="mockup-spotlight-hint"
              className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full border border-violet-500/40 bg-gradient-to-r from-violet-500/12 via-blue-500/12 to-emerald-500/12 px-4 py-1.5 text-xs font-bold tracking-tight shadow-sm shadow-violet-500/20 backdrop-blur transition-all hover:-translate-y-0.5 hover:shadow-md hover:shadow-violet-500/30 sm:text-sm"
            >
              <span className="absolute inset-y-0 -left-12 w-12 -skew-x-12 bg-white/40 opacity-0 transition-all duration-700 group-hover:translate-x-[420%] group-hover:opacity-100 dark:bg-white/15" />
              <motion.span
                animate={{ x: [0, 5, 0], rotate: [0, -10, 0] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                className="inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-gradient-to-br from-violet-600 to-blue-600 text-white shadow-sm shadow-violet-600/30"
              >
                <MousePointer2 className="h-3.5 w-3.5" />
              </motion.span>
              <span className="text-muted-foreground">Try</span>
              <span className="bg-gradient-to-r from-violet-600 via-blue-600 to-emerald-600 bg-clip-text text-transparent dark:from-violet-300 dark:via-blue-300 dark:to-emerald-300">
                {spotScene.label}
              </span>
              <span className="hidden text-foreground/70 sm:inline">— {spotScene.hook}</span>
              <ChevronRight className="h-3.5 w-3.5 text-violet-500" />
            </motion.button>
          ) : (
            <motion.div
              key="now-exploring"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.35 }}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-[11px] font-semibold text-muted-foreground"
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
              </span>
              Exploring · <span className="font-bold text-foreground">{active.label}</span>
              <button
                type="button"
                onClick={() => setPaused(false)}
                data-testid="mockup-resume-inline"
                className="ml-1 inline-flex items-center gap-1 rounded-full bg-secondary/70 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-foreground/80 transition-colors hover:text-foreground"
              >
                <Play className="h-2.5 w-2.5 fill-current" /> Auto-tour
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="pointer-events-none absolute -inset-10 -z-10 rounded-[2.5rem] bg-gradient-to-tr from-violet-500/15 via-blue-500/10 to-emerald-500/10 blur-3xl" />

      <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-2xl ring-1 ring-inset ring-white/5">
        {/* Window chrome */}
        <div className="flex items-center justify-between border-b border-border bg-gradient-to-b from-secondary/60 to-secondary/30 px-5 py-3">
          <div className="flex items-center gap-1.5">
            <span className="h-3 w-3 rounded-full bg-rose-400 shadow-sm" />
            <span className="h-3 w-3 rounded-full bg-amber-400 shadow-sm" />
            <span className="h-3 w-3 rounded-full bg-emerald-400 shadow-sm" />
          </div>
          <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
            <span className="font-display tracking-tight">Clinic OS</span>
            <ChevronRight className="h-3 w-3 text-foreground/30" />
            <span className="font-medium text-foreground/85">{active.label}</span>
          </div>
          <div className="flex items-center gap-2">
            {paused && (
              <button
                type="button"
                onClick={() => setPaused(false)}
                aria-label="Resume auto-cycle"
                data-testid="scene-resume"
                className="hidden items-center gap-1 rounded-md border border-border bg-card px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground sm:inline-flex"
              >
                <Play className="h-2.5 w-2.5 fill-current" /> Resume
              </button>
            )}
          </div>
        </div>

        {/* Interactive tab bar */}
        <div
          className="flex items-center gap-1.5 overflow-x-auto border-b border-border bg-secondary/20 px-3 py-2 scrollbar-thin"
          data-testid="scene-tabs"
          role="tablist"
        >
          {SCENES.map((s, i) => {
            const isActive = i === idx;
            const isSpotlight = !paused && !isActive && i === safeSpotIdx;
            return (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => onSelectScene(i)}
                data-testid={`scene-tab-${s.id}`}
                className={`group relative inline-flex flex-none items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-bold tracking-tight transition-all sm:text-xs ${
                  isActive
                    ? "bg-gradient-to-r from-violet-600 to-blue-600 text-white shadow-md shadow-violet-600/25 ring-1 ring-inset ring-white/15"
                    : isSpotlight
                      ? "bg-violet-500/10 text-violet-700 ring-2 ring-violet-500/60 ring-offset-2 ring-offset-secondary/20 dark:text-violet-300"
                      : "text-muted-foreground hover:bg-card hover:text-foreground"
                }`}
              >
                {isSpotlight && (
                  <span className="pointer-events-none absolute inset-0 -z-0 animate-pulse rounded-lg bg-violet-500/15" />
                )}
                {isActive && (
                  <span className="relative flex h-1.5 w-1.5 flex-none">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-300 opacity-75" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-300" />
                  </span>
                )}
                {isSpotlight && (
                  <span className="relative flex h-1.5 w-1.5 flex-none">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-violet-400 opacity-75" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-violet-500" />
                  </span>
                )}
                <span className="relative whitespace-nowrap">{s.label}</span>
              </button>
            );
          })}
          <div className="ml-auto hidden flex-none items-center gap-1.5 pl-2 text-[10px] font-semibold text-muted-foreground/70 lg:flex">
            <span className="inline-flex h-1.5 w-1.5 rounded-full bg-foreground/30" />
            <span className="whitespace-nowrap uppercase tracking-[0.16em]">
              {paused ? "Paused" : "Auto-tour"}
            </span>
          </div>
        </div>

        {/* Inner workspace */}
        <div className="relative h-[420px] overflow-hidden bg-background p-4 sm:h-[520px] sm:p-5 md:h-[580px] md:p-7">
          <AnimatePresence mode="wait">
            <motion.div
              key={active.id}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.95, ease: [0.4, 0, 0.2, 1] }}
              className="h-full"
              data-testid={`scene-${active.id}`}
            >
              <Scene />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Caption */}
        <div className="flex items-center justify-between border-t border-border bg-gradient-to-b from-secondary/30 to-secondary/60 px-5 py-3 text-xs">
          <AnimatePresence mode="wait">
            <motion.span
              key={active.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="font-medium text-muted-foreground"
            >
              {SCENE_CAPTION[active.id]}
            </motion.span>
          </AnimatePresence>
          <span className="hidden items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70 sm:inline-flex">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
            </span>
            v3.1 · live
          </span>
        </div>
      </div>
    </div>
  );
};
